import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CryptoJS from "crypto-js";
import { RootState } from "@/redux/store";
import { Card } from "@/components/ui/card";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { useGetAllMemberQuery } from "@/services/memberApi";
import { Check, ChevronDownIcon, ChevronsUpDown, Search } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  Fragment,
} from "react";
import { useSelector } from "react-redux";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { isAddress } from "viem";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useGetMembershipsQuery } from "@/services/membershipsApi";
import { Separator } from "@/components/ui/separator";
import { displayDate, formatToPKR } from "@/lib/helper";
import { useDebounce } from "@/hooks/use-debounce";
import { ErrorType } from "@/types";
import { Transaction, SellableItem } from "@/types/pos/transaction";
import { MemberTableDatatypes } from "@/types/member";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import {
  FormProvider,
  useForm,
  useWatch,
  UseFormSetValue,
} from "react-hook-form";
import { useGetOrgTaxTypeQuery } from "@/services/organizationApi";
import {
  useCreateTransactionMutation,
  useGetTransactionByIdQuery,
  useGetTransactionQuery,
  useUpdateTransactionMutation,
} from "@/services/transactionApi";
import MemberForm from "../../members/modal/form";
import Checkout from "./checkout";
import ParkReceipt from "./park-receipt";
import ProductCard from "./product-card";
import EventCard from "./event-card";
import { calculateItemPrice } from "@/lib/pricing-utils";
import FacilitySlots from "./facility-slots";
import { useGetStaffListQuery } from "@/services/staffsApi";
import { useGetlastRegisterSessionQuery } from "@/services/registerApi";
import useDocumentTitle from "@/components/ui/common/document-title";
import {
  Gender,
  ProductStatus,
  POSProductType,
  SaleStatus,
  TaxType,
} from "@/types/enums";
import { CustomerCombobox } from "@/components/ui/customer-combobox/customer-combobox";
import { useGetAllProductsQuery } from "@/services/productApi";
import { Product, ProductType } from "@/types/system-setting/product";
import { useGetSystemSettingsQuery } from "@/services/systemConfigApi";
import { useGetEventsQuery } from "@/services/eventApi";
import { FacilitySchedule } from "@/types/facility/schedule";
import { useLazyCriteriaValidationQuery } from "@/services/facilityApis";
import { Membership } from "@/types/system-setting/membership";
import { calculateTaxFromTaxGroups } from "@/lib/tax-utils";
import { useGetTaxGroupQuery, useGetSalesTaxListQuery, useLazyGetSalesTaxByIdQuery } from "@/services/salesTaxApi";
import { useLazyGetPriceMasterByIdQuery } from "@/services/priceMasterApi";
import { TaxGroup } from "@/types/system-setting/tax-group";
import { SaleTax } from "@/types/system-setting/sale-tax";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { TaxGroupType } from "@/types/enums";
const {
  VITE_REGISTER_MAX_HOUR,
  VITE_ENCYRPTION_KEY,
  VITE_SECRET_KEY,
  VITE_BLOCKCHAIN_API_URL,
} = import.meta.env;

interface SearchCriteriaType {
  status: string;
  sort_order: string;
  sort_key: string;
  search_key?: string;
}

type ProductCategory = {
  type: string;
  label: string;
  products: any[];
  empty: string;
};

// Extended SellableItem for internal calculations (includes tax info)
type ExtendedSellableItem = SellableItem & {
  // Tax calculation fields (not sent to API)
  tax_rate?: number;
  tax_type?: string;
  discount_percentage?: number;
  base_price?: number; // Base price before tax
  // Display fields (not sent to API)
  description?: string;
  tax_name?: string;
  // Facility slot data (not sent to API)
  facility_slot_data?: any;
  item_tax_group?: TaxGroup | any;
  price_type?: "base" | "special" | "plan" | "group";
  applied_pricing_name?: string;
};

const has24HoursPassed = (date: Date) => {
  const now = new Date();
  const differenceInMs = now.getTime() - date.getTime();
  const differenceInHours = differenceInMs / (1000 * 60 * 60);
  return differenceInHours >= VITE_REGISTER_MAX_HOUR;
};

const Sell = () => {
  useDocumentTitle("Sell");
  const { id } = useParams();
  const hide_pos_discount = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .hide_pos_discount ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const member = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).member ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const navigate = useNavigate();

  const isContinue =
    JSON.parse(localStorage.getItem("isContinue") as string) || null;
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const invoice = useSelector((state: RootState) => state.invoice.invoice);
  const walletTransaction = useSelector(
    (state: RootState) => state.wallet.invoice,
  );
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { data: otherSettingsData, refetch: settingsRefetch } =
    useGetSystemSettingsQuery({
      query: "groups=other_settings",
      orgId: orgId
    });

  const other_settings = useMemo(
    () => otherSettingsData?.other_settings as Record<string, string>,
    [otherSettingsData],
  );

  const counter_number =
    (localStorage.getItem("counter_number") as string) == ""
      ? null
      : Number(localStorage.getItem("counter_number") as string);

  const {
    data: lastSession,
    refetch: lastSessionRefetch,
    isError,
    isFetching,
    error: lastSessionError,
  } = useGetlastRegisterSessionQuery(counter_number as number, {
    refetchOnMountOrArgChange: true,
  });

  const { data: taxGroupsData } = useGetTaxGroupQuery({ query: "", org_id: orgId }, {
    skip: !orgId,
  });

  const memberTaxGroups = useMemo(() => {
    return Array.isArray(taxGroupsData?.data)
      ? taxGroupsData?.data.filter((tg) => tg.type === TaxGroupType.Member)
      : [];
  }, [taxGroupsData]);

  const [getPriceMasterById] = useLazyGetPriceMasterByIdQuery();

  // initial value before sale is parked or sold
  const initialValues: Transaction = {
    org_id: orgId,
    counter_id: counter_number as number, //counter id
    batch_id: lastSession?.id as number, //register id
    staff_id: userInfo?.user.id,
    staff_name: userInfo?.user.first_name,
    submitted_to_authority: true,
    member_id: null,
    member_name: null,
    member_email: null,
    member_address: null,
    member_gender: null,
    member_nic: undefined,
    wallet_address: null,
    notes: "",
    refund_details: [
      {
        refund_notes: null,
        created_at: null,
        created_by: null,
      },
    ],
    refund_notes: "",
    receipt_number:
      other_settings?.invoice_prefix + Math.floor(Math.random() * 99), //will implement with prefix module comes
    tax_number: Math.floor(Math.random() * 99), //from srb
    discount: 0,
    total: 0,
    srb_invoice_id: undefined,
    sub_total: 0,
    tax_amount: 0,
    is_business: false,
    amount_received: 0,
    change_amount: 0,
    status: ProductStatus.Pending,
    transaction_type: SaleStatus.Sale,
    items: [],
    payments: [],
    member_tax_group_id: 0,
  };

  const form = useForm<Transaction>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    watch,
    register,
    setValue,
    reset,
    formState: { errors },
  } = form;
  const watcher = watch();

  const [productPayload, setProductPayload] = useState<ExtendedSellableItem[]>(
    [],
  );
  const [showCheckout, setShowCheckout] = useState<boolean>(false);
  const [dayExceeded, setDayExceeded] = useState<boolean>(false);
  const isLoadingDataRef = useRef(false); // Ref to track if we're loading data programmatically

  useEffect(() => {
    if (invoice) {
      isLoadingDataRef.current = true; // Mark that we're loading data programmatically
      const updatedPayload = {
        ...invoice,
        invoice_id: invoice.id,
        receipt_number:
          other_settings?.invoice_prefix + Math.floor(Math.random() * 99), //will implement with prefix module comes
        notes: "",
        staff_id: userInfo?.user.id,
        submitted_to_authority: true,
        srb_invoice_id: undefined,
        staff_name: userInfo?.user.first_name,
        counter_id: counter_number as number, //counter id
        batch_id: lastSession?.id as number,
        status: ProductStatus.Pending,
        transaction_type: SaleStatus.Sale,
        tax_number: Math.floor(Math.random() * 99), //from srb
        member_gender: invoice.member_gender as Gender,
        total: Math.round(invoice.total),
        payments: [],
      };
      delete updatedPayload.id;
      console.log("updatedPayload", { updatedPayload });

      // Ensure items have the discount_percentage property for proper calculations
      const itemsWithDiscountPercentage = (invoice.items || []).map(
        (item: any) => {
          let processedItem = {
            ...item,
            // For loaded invoices, we need to calculate discount_percentage from the absolute discount
            // discount_percentage = (discount / (price * quantity)) * 100
            discount_percentage:
              item.discount_percentage ||
              (item.discount && item.price && item.quantity
                ? (item.discount / (item.price * item.quantity)) * 100
                : 0),
          };

          // Handle inclusive vs exclusive tax types properly
          if (item.tax_type === "inclusive") {
            // For inclusive tax invoices:
            // - price should be the tax-inclusive price (total)
            // - net_price should be the base price (sub_total)
            // - total should be the tax-inclusive price
            processedItem.price = item.total || item.price; // Use total as the tax-inclusive price
            processedItem.net_price =
              item.sub_total || item.net_price || item.price; // Base price
          } else {
            // For exclusive tax invoices:
            // - price should be the base price
            // - net_price should be the base price
            // - total should be base price + tax
            processedItem.price = item.price || item.sub_total; // Base price
            processedItem.net_price =
              item.net_price || item.price || item.sub_total; // Base price
          }

          return processedItem;
        },
      );

      updatedPayload.items = itemsWithDiscountPercentage;
      console.log({ updatedPayload });

      // Convert to ExtendedSellableItem format
      const extendedInvoiceItems: ExtendedSellableItem[] =
        itemsWithDiscountPercentage.map((item: any) => ({
          ...item,
          base_price: item.price || item.base_price || item.net_price,
          description: item.description || "Item",
          tax_rate: item.tax_rate || 0,
          tax_type: item.tax_type || "exclusive",
          discount_percentage: item.discount_percentage || 0,
          // For loaded invoices, preserve the original discount amount
          discount: item.discount || 0,
        }));

      // Set product payload first
      setProductPayload(extendedInvoiceItems);

      // Reset the form with the updated payload FIRST
      reset(updatedPayload, {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: true, // Keep default values as provided by initialValue
      });

      // THEN set the customer (after form is populated)
      if (memberListData && memberListData?.length > 0) {
        const member = memberListData?.find(
          (member) => member.id == updatedPayload.member_id,
        ) as MemberTableDatatypes;
        if (member) setCustomer(member);
      }

      // For inclusive tax invoices, we need to use the original invoice values
      // to avoid recalculation issues
      if (invoice.items?.some((item: any) => item.tax_type === "inclusive")) {
        // Set form values directly from invoice for inclusive tax
        setValue("sub_total", Math.round(invoice.sub_total));
        setValue("tax_amount", Math.round(invoice.tax_amount));
        setValue("total", Math.round(invoice.total));
        setValue("discount", invoice.discount || 0);

        // Update the payload to match invoice values
        const updatedItems = itemsWithDiscountPercentage.map((item: any) => ({
          ...item,
          sub_total: item.price * item.quantity, // Use base price * quantity
          tax_amount: Math.round(invoice.tax_amount), // Use invoice tax amount
          total: item.price, // For inclusive, total equals the tax-inclusive price
        }));

        setProductPayload(extendedInvoiceItems);
        setValue("items", updatedItems);
      } else {
        // For exclusive tax, run normal calculations
        // Trigger calculation after state has been set
        setTimeout(() => {
          updateFormValues(extendedInvoiceItems, { clampDiscount: true });
        }, 0);
      }

      setShowCheckout(true);

      // Sync selectedMemberTaxGroup with the loaded invoice's tax group to prevent overwrite by other effects
      if (invoice.member_tax_group_id && memberTaxGroups?.length > 0) {
        const matchingGroup = memberTaxGroups.find((tg: any) => tg.id === invoice.member_tax_group_id);
        if (matchingGroup) {
          setSelectedMemberTaxGroup(matchingGroup);
        }
      }

      // Reset loading flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isLoadingDataRef.current = false;
      }, 100);
    }
  }, [invoice, memberTaxGroups]);

  useEffect(() => {
    console.log("Wallet Transaction:", walletTransaction);

    if (walletTransaction) {
      isLoadingDataRef.current = true; // Mark that we're loading data programmatically
      const updatedPayload = {
        ...walletTransaction,
        receipt_number:
          other_settings?.invoice_prefix + Math.floor(Math.random() * 99),
        notes: walletTransaction.notes || "",
        staff_id: userInfo?.user.id,
        staff_name: userInfo?.user.first_name,
        counter_id: counter_number as number,
        batch_id: lastSession?.id as number,
        status: ProductStatus.Pending,
        transaction_type:
          (walletTransaction.transaction_type as SaleStatus) ||
          SaleStatus.Refund,
        tax_number: Math.floor(Math.random() * 99),
        member_gender: walletTransaction.member_gender || Gender.Male,
        // Ensure these values are properly set
        total: Number(walletTransaction.total) || 0,
        sub_total: Number(walletTransaction.sub_total) || 0,
        tax_amount: Number(walletTransaction.tax_amount) || 0,
        discount: Number(walletTransaction.discount) || 0,
        srb_invoice_id: undefined,
        payments: [],
      };

      console.log("Updated Payload:", updatedPayload);

      // Ensure items have the discount_percentage property for proper calculations
      const itemsWithDiscountPercentage = (walletTransaction.items || []).map(
        (item: any) => ({
          ...item,
          // For loaded wallet transactions, calculate discount_percentage from absolute discount if present
          discount_percentage:
            item.discount_percentage ||
            (item.discount && item.price && item.quantity
              ? (item.discount / (item.price * item.quantity)) * 100
              : 0),
          net_price: item.net_price || item.price, // Initialize net_price if not present
        }),
      );

      updatedPayload.items = itemsWithDiscountPercentage;

      // Convert to ExtendedSellableItem format
      const extendedWalletItems: ExtendedSellableItem[] =
        itemsWithDiscountPercentage.map((item: any) => ({
          ...item,
          base_price: item.price || item.base_price || item.net_price,
          description: item.description || "Item",
          tax_rate: item.tax_rate || 0,
          tax_type: item.tax_type || "exclusive",
          discount_percentage: item.discount_percentage || 0,
          // For loaded wallet transactions, preserve the original discount amount
          discount: item.discount || 0,
        }));

      // Set product payload first
      setProductPayload(extendedWalletItems);

      // Reset form with new values FIRST - make sure all required fields are included
      reset(updatedPayload, {
        keepIsSubmitted: false,
        keepSubmitCount: false,
        keepDirtyValues: false,
        keepDefaultValues: true,
      });

      // THEN find and set customer
      if (memberListData && memberListData?.length > 0) {
        const member = memberListData?.find(
          (member) => member.id === updatedPayload.member_id,
        ) as MemberTableDatatypes;
        if (member) {
          setCustomer(member);
        }
      }

      // Trigger calculation after state has been set
      setTimeout(() => {
        updateFormValues(extendedWalletItems, { clampDiscount: true });
      }, 0);

      setShowCheckout(true);

      // Sync selectedMemberTaxGroup with the loaded wallet transaction's tax group to prevent overwrite by other effects
      if (walletTransaction.member_tax_group_id && memberTaxGroups?.length > 0) {
        const matchingGroup = memberTaxGroups.find((tg: any) => tg.id === walletTransaction.member_tax_group_id);
        if (matchingGroup) {
          setSelectedMemberTaxGroup(matchingGroup);
        }
      }

      // Reset loading flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isLoadingDataRef.current = false;
      }, 100);
    }
  }, [walletTransaction, memberTaxGroups]);

  // search product
  const initialSearchCriteria: SearchCriteriaType = {
    status: "active",
    sort_order: "desc",
    sort_key: "id",
  };

  const [searchCriteria, setSearchCriteria] = useState<SearchCriteriaType>(
    initialSearchCriteria,
  );

  // Initialize query from initial searchCriteria
  const initialQuery = (() => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(initialSearchCriteria)) {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            params.append(key, val);
          });
        } else {
          params.append(key, value);
        }
      }
    }
    return params.toString();
  })();

  const [query, setQuery] = useState(initialQuery);
  const [inputValue, setInputValue] = useState("");
  const [maxProductsQauntity, setMaxProductsQauntity] = useState<
    Record<number, number>
  >({});
  const debouncedInputValue = useDebounce(inputValue, 500);
  // select customer
  const [customer, setCustomer] = useState<MemberTableDatatypes | null>(null);
  const [openParkSale, setParkSale] = useState(false);
  const [openStaffDropdown, setStaffDropdown] = useState(false);
  const [action, setAction] = useState<"add" | "edit" | "member-created">(
    "add",
  );
  const [openMemberForm, setOpenMemberForm] = useState<boolean>(false);
  const [editMember, setEditMember] = useState<MemberTableDatatypes | null>(
    null,
  );
  const [productTypeFilter, setProductTypeFilter] = useState<ProductType>(
    ProductType.OtherProduct,
  );
  const [activeTab, setActiveTab] = useState<POSProductType>(
    POSProductType.Membership,
  );

  const { data: salesTaxList } = useGetSalesTaxListQuery(Number(orgId), {
    skip: !orgId,
  });

  const allSalesTaxes = useMemo(() => {
    return Array.isArray(salesTaxList) ? salesTaxList as SaleTax[] : [];
  }, [salesTaxList]);
  console.log({ salesTaxList })

  const [selectedMemberTaxGroup, setSelectedMemberTaxGroup] = useState<TaxGroup | null>(null);

  // Sync selectedMemberTaxGroup with customer's tax group
  useEffect(() => {
    if (isLoadingDataRef.current) return;
    if (customer) {
      const customerTaxGroup = Array.isArray(customer.tax_group)
        ? customer.tax_group[0]
        : customer.tax_group;

      if (customerTaxGroup) {
        setSelectedMemberTaxGroup(customerTaxGroup);
      } else {
        setSelectedMemberTaxGroup(null);
      }
    } else {
      setSelectedMemberTaxGroup(null);
    }
  }, [customer]);

  // Push the ID of the selected tax group to the form state
  useEffect(() => {
    setValue("member_tax_group_id", selectedMemberTaxGroup?.id || 0);
  }, [selectedMemberTaxGroup, setValue]);

  const [getSalesTaxById] = useLazyGetSalesTaxByIdQuery();

  const resolveTax = async (productTaxGroup: any, memberTaxGroup: any) => {
    let { tax, taxRate, taxName } = calculateTaxFromTaxGroups(productTaxGroup, memberTaxGroup);

    if (!tax && productTaxGroup?.default_tax_id) {
      // First check if it's already in the product tax group's taxes array
      const inlineTax = productTaxGroup.taxes?.find((t: any) => t.id === productTaxGroup.default_tax_id);
      if (inlineTax) {
        return { taxRate: inlineTax.percentage, taxName: inlineTax.name, tax: inlineTax };
      }

      // Then fetch from API
      if (allSalesTaxes.length > 0) {
        const matchingTax = allSalesTaxes.find((t) => t.id === productTaxGroup.default_tax_id);
        if (matchingTax) {
          return { taxRate: matchingTax.percentage, taxName: matchingTax.name, tax: matchingTax };
        }
      }
      try {
        const defaultTaxResponse = await getSalesTaxById({ sale_tax_id: productTaxGroup.default_tax_id, orgId }).unwrap();
        if (defaultTaxResponse) {
          return { taxRate: defaultTaxResponse.percentage, taxName: defaultTaxResponse.name, tax: defaultTaxResponse };
        }
      } catch (err) {
        console.error("Failed to fetch default tax", err);
      }
    }

    return { taxRate, taxName, tax };
  };

  // Recalculate cart when selectedMemberTaxGroup changes
  useEffect(() => {
    if (productPayload.length > 0) {
      const recalculate = async () => {
        const updatedPayload = await Promise.all(productPayload.map(async (item) => {
          // Find the product's tax group to recalculate with the NEW selectedMemberTaxGroup
          // Use the stored item_tax_group for recalculation
          const productTaxGroup = item.item_tax_group;

          // If no tax group information available (e.g. loaded invoice), preserve existing tax rate
          if (!productTaxGroup) {
            return item;
          }

          const { taxRate, taxName } = await resolveTax(productTaxGroup, selectedMemberTaxGroup || null);

          return {
            ...item,
            tax_rate: taxRate,
            tax_name: taxName,
          };
        }));

        setProductPayload(updatedPayload);
        updateFormValues(updatedPayload, { clampDiscount: true });
      };
      recalculate();
    }
  }, [selectedMemberTaxGroup]);

  // listing apis
  const { data: staffList } = useGetStaffListQuery(orgId);

  const { data: orgTaxTypeData } = useGetOrgTaxTypeQuery(Number(orgId), {
    skip: !orgId,
  });

  const orgTaxType = orgTaxTypeData?.tax_type as TaxType;

  // Lazy query for facility criteria validation
  const [validateCriteria, { isLoading: isValidatingCriteria }] =
    useLazyCriteriaValidationQuery();

  // Keep current cart's tax_type aligned with org settings (products/invoices don't reliably include tax_type)
  useEffect(() => {
    if (!orgTaxType) return;
    if (productPayload.length === 0) return;
    setProductPayload((prev) => {
      const next = prev.map((p) => ({ ...p, tax_type: orgTaxType }));
      // Recalculate totals against the updated org tax type
      updateFormValues(next, { clampDiscount: true });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgTaxType]);

  const { data: memberhsipList, refetch: refetchMemberships } =
    useGetMembershipsQuery(
      {
        org_id: orgId,
        query: query + "&type=default&load_pricing=true",
      },
      {
        skip: !orgId || activeTab !== POSProductType.Membership,
      },
    );


  const { data: creditList, refetch: refetchCreditAddOns } =
    useGetMembershipsQuery(
      {
        org_id: orgId,
        query: query + "&type=credit_add_ons&load_pricing=true",
      },
      {
        skip: !orgId || activeTab !== POSProductType.CreditAddOns,
      },
    );

  const { data: productList, refetch: refetchProducts } =
    useGetAllProductsQuery(
      {
        org_id: orgId,
        query: query + "&load_available=true&load_pricing=true",
      },
      {
        skip: !orgId || activeTab !== POSProductType.Product,
      },
    );
  // Build events query with both statuses and optional search
  const eventsQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.append("status", "upcoming");
    params.append("status", "ongoing");
    params.append("load_available", "true");
    params.append("load_pricing", "true");
    params.append("type", "events");
    // Add search query if it exists
    if (query) {
      const searchParams = new URLSearchParams(query);
      for (const [key, value] of searchParams.entries()) {
        if (key !== "status" && key !== "type") {
          params.append(key, value);
        }
      }
    }
    return params.toString();
  }, [query]);

  const { data: eventsList, refetch: refetchEvents } = useGetEventsQuery(
    {
      org_id: orgId,
      query: eventsQuery,
    },
    {
      skip: !orgId || activeTab !== POSProductType.Event,
    },
  );

  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
const debouncedCustomerSearch = useDebounce(customerSearchTerm, 500);

// Existing query (keep for initial load)
const { data: memberList, refetch: memberRefetch } = useGetAllMemberQuery(
  {
    org_id: orgId,
    query: debouncedCustomerSearch
      ? `sort_key=id&sort_order=desc&search_key=${debouncedCustomerSearch}`
      : "sort_key=id&sort_order=desc&limit=20",
  },
  {
    skip: showCheckout,
  },
);

  const [createTransaction] = useCreateTransactionMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [transactionId, setTransactionId] = useState<number | undefined>(
    id ? Number(id) : undefined,
  );
  const { data: retriveSaleData, refetch: retriveSaleRefetch } =
    useGetTransactionByIdQuery(
      {
        transaction_id: transactionId as number,
        org_id: orgId,
        // counter_id: watcher.counter_id,
      },
      {
        skip: transactionId == undefined,
      },
    );

  // get unpaid sales to retrive sale
  const { data: transactionData, refetch: transactionRefetch } =
    useGetTransactionQuery(
      {
        counter_id: counter_number as number,
        org_id: orgId,
        query: `status=Pending&sort_order=desc`,
      },
      {
        skip: counter_number == null,
      },
    );

  const transactiontableData = useMemo(() => {
    return Array.isArray(transactionData?.data) ? transactionData?.data : [];
  }, [transactionData]);

  const memberListData = useMemo(() => {
    return Array.isArray(memberList?.data) ? memberList?.data : [];
  }, [memberList]);

  const memberhsipListData = useMemo(() => {
    return Array.isArray(memberhsipList?.data) ? memberhsipList?.data : [];
  }, [memberhsipList]);

  const creditListData = useMemo(() => {
    return Array.isArray(creditList?.data) ? creditList?.data : [];
  }, [creditList]);

  const productListData = useMemo(() => {
    return Array.isArray(productList?.data) ? productList?.data : [];
  }, [productList]);

  const eventsListData = useMemo(() => {
    return Array.isArray(eventsList?.data) ? eventsList?.data : [];
  }, [eventsList]);

  // Note: normalizeProducts is not used for POS payload, products are handled directly in addProduct
  const normalizeProducts = (products: Product[] = []): any[] =>
    products.map((product) => ({
      ...product,
      name: product.product_name,
      net_price: product.price,
    }));

  const normalizedProductList = useMemo(() => {
    return normalizeProducts(productListData as Product[]);
  }, [productListData]);

  // Normalize events for POS - keep full event data for pricing calculations
  const normalizeEvents = (
    events: FacilitySchedule[] = [],
  ): FacilitySchedule[] =>
    events.map((event) => {
      // Return the full event object so we can access plan_pricing, special_pricing, etc.
      return event;
    });

  const normalizedEventsList = useMemo(() => {
    return normalizeEvents(eventsListData as FacilitySchedule[]);
  }, [eventsListData]);

  const filteredProductList = useMemo(() => {
    return normalizedProductList.filter(
      (product: any) => product.type === productTypeFilter,
    );
  }, [normalizedProductList, productTypeFilter]);

  const productTypeTabs = [
    {
      value: ProductType.Wallet,
      label: "Wallet",
    },
    {
      value: ProductType.OtherProduct,
      label: "Other Product",
    },
  ];

  // if navigating back to sell page, the checkout screen must reset
  useEffect(() => {
    const handlePopstate = () => {
      setInputValue(""); // Clear the search input
      refetchMemberships();
      refetchCreditAddOns();
      refetchProducts();
      refetchEvents();
    };

    window.addEventListener("popstate", handlePopstate);

    if (!id && !invoice && !walletTransaction) {
      reset(initialValues as Transaction);
      setCustomer(null);
      setProductPayload([]);
      setShowCheckout(false);
      setIsDiscountManuallySet(false);
      setInputValue(""); // Clear the search input
    }
    if (lastSession) {
      setValue("batch_id", lastSession.id as number);
    }

    return () => {
      window.removeEventListener("popstate", handlePopstate);
    };
  }, [
    id,
    lastSession,
    invoice,
    refetchMemberships,
    refetchCreditAddOns,
    refetchProducts,
    refetchEvents,
  ]);

  // for past 24 hour validation
  useEffect(() => {
    if (counter_number !== null) {
      lastSessionRefetch();
    }

    if (isError) {
      const errorCode =
        typeof lastSessionError === "object" &&
        "status" in lastSessionError &&
        (lastSessionError as ErrorType).status;

      if (errorCode == 404) {
        toast({
          variant: "destructive",
          title: "No active register session found.",
        });
        navigate(`/admin/pos/register`);
        return;
      }
    } else if (!isFetching && lastSession?.closing_time !== null) {
      toast({
        variant: "destructive",
        title: "No active register session found.",
      });
      navigate(`/admin/pos/register`);
      return;
    } else {
      const opening_time = new Date(lastSession?.opening_time as string);
      //
      if (has24HoursPassed(opening_time) && !isContinue) {
        setDayExceeded(true);
      } else {
        setDayExceeded(false);
      }
    }
  }, [lastSession, isError, lastSessionError, lastSessionRefetch]);

  // for search products
  useEffect(() => {
    setSearchCriteria((prev) => {
      const newCriteria = { ...prev };
      if (debouncedInputValue.trim() !== "") {
        newCriteria.search_key = debouncedInputValue;
      } else {
        delete newCriteria.search_key;
      }
      return newCriteria;
    });
  }, [debouncedInputValue, setSearchCriteria]);

  // for filters to add in query params
  useEffect(() => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(searchCriteria)) {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            params.append(key, val); // Append each array element as a separate query parameter
          });
        } else {
          params.append(key, value); // For non-array values
        }
      }
    }
    const newQuery = params.toString();
    setQuery(newQuery);
  }, [searchCriteria]);

  function handleOpenForm() {
    setAction("add");
    setEditMember(null);
    setOpenMemberForm(true);
  }
  // product lists by category
  const productCategories = useMemo<ProductCategory[]>(
    () => [
      {
        type: POSProductType.Membership,
        label: "Memberships",
        products: memberhsipListData as Membership[],
        empty: "No Memberships found",
      },
      {
        type: POSProductType.Event,
        label: "Events",
        products: normalizedEventsList,
        empty: "No Events found",
      },
      {
        type: POSProductType.Product,
        label: "Products",
        products: filteredProductList,
        empty:
          productTypeFilter === ProductType.Wallet
            ? "No wallet products found"
            : "No other products found",
      },
      {
        type: "facilities",
        label: "Facility Slots",
        products: [], // This will be handled by the FacilitySlots component
        empty: "Select facility and slot above",
      },
      {
        type: POSProductType.CreditAddOns,
        label: "Credits",
        products: creditListData as Membership[],
        empty: "No Credits found",
      },
    ],
    [
      memberhsipListData,
      filteredProductList,
      productTypeFilter,
      normalizedEventsList,
      creditListData,
    ],
  );

  const updateFormValues = (
    updatedPayload: ExtendedSellableItem[],
    options?: {
      clampDiscount?: boolean;
      updatePayload?: boolean;
      manualDiscountOverride?: number;
      isDiscountManuallySetOverride?: boolean;
    },
  ) => {
    // ... existing safety check ...
    if (
      !updatedPayload ||
      !Array.isArray(updatedPayload) ||
      updatedPayload.length === 0
    ) {
      return;
    }

    const effectiveIsDiscountManuallySet =
      options?.isDiscountManuallySetOverride !== undefined
        ? options.isDiscountManuallySetOverride
        : isDiscountManuallySet;

    // Calculate totals from raw data
    let rawSubtotal = 0;
    let rawProductDiscount = 0;

    // First pass: calculate subtotals and product discounts
    const itemsWithBasicCalcs = updatedPayload.map((prod) => {
      const unitPrice = prod.base_price ?? prod.price ?? 0;
      const itemSubtotal = unitPrice * prod.quantity;
      rawSubtotal += itemSubtotal;

      const discountPercentage = prod.discount_percentage || 0;
      const itemDiscount = (itemSubtotal * discountPercentage) / 100;
      rawProductDiscount += itemDiscount;

      return {
        ...prod,
        itemSubtotal,
        itemDiscount,
        discountPercentage,
        unitPrice,
      };
    });

    // Determine manual discount
    const shouldClamp = options?.clampDiscount ?? true;
    const currentManualDiscount =
      options?.manualDiscountOverride !== undefined
        ? options.manualDiscountOverride
        : discountAmt || 0;
    const minAllowedDiscount = Math.round(rawProductDiscount);
    const maxAllowedDiscount = Math.max(0, Math.round(rawSubtotal) - 1);
    const clampedManualDiscount = Math.min(
      Math.max(currentManualDiscount, minAllowedDiscount),
      maxAllowedDiscount,
    );
    const effectiveManualDiscount = shouldClamp
      ? clampedManualDiscount
      : currentManualDiscount;

    if (
      shouldClamp &&
      clampedManualDiscount !== currentManualDiscount &&
      options?.manualDiscountOverride === undefined
    ) {
      setValue("discount", clampedManualDiscount, { shouldValidate: false });
    }

    // Console logging for debugging
    // console.log("=== SUBTOTAL CALCULATION DEBUG ===");
    // console.log("Raw Subtotal (sum of all item prices * quantities):", rawSubtotal);
    // console.log("Raw Product Discount (sum of all product discounts):", rawProductDiscount);
    // console.log("Current Manual Discount:", currentManualDiscount);
    // console.log("Effective Manual Discount:", effectiveManualDiscount);
    // console.log("Min Allowed Discount (product discount total):", minAllowedDiscount);

    // Check if manual discount is actually overridden (not just matching product discount)
    const isManualDiscountOverride =
      isDiscountManuallySet && effectiveManualDiscount > minAllowedDiscount;

    // console.log("Is Manual Discount Override:", isManualDiscountOverride);

    let rawTotalTax = 0;
    let rawTotalAfterDiscounts = 0;

    // Second pass: calculate tax after ALL discounts (product + manual)
    const updatedItemsWithCalculations = itemsWithBasicCalcs.map(
      (
        prod: ExtendedSellableItem & {
          itemSubtotal: number;
          itemDiscount: number;
          discountPercentage: number;
          unitPrice: number;
        },
      ) => {
        const itemSubtotal = prod.itemSubtotal;
        const itemProductDiscount = prod.itemDiscount;

        // Calculate proportional share based on ORIGINAL price ratio
        const itemProportionalShare =
          rawSubtotal > 0 ? itemSubtotal / rawSubtotal : 0;

        // Allocate the ENTIRE manual discount proportionally based on price ratios
        const itemAllocatedManualDiscount =
          effectiveManualDiscount * itemProportionalShare;

        // ... Determine the actual discount to apply
        const totalItemDiscount = effectiveIsDiscountManuallySet
          ? itemAllocatedManualDiscount
          : itemProductDiscount;

        // Ensure discount is never negative and doesn't exceed item subtotal
        const finalItemDiscount = Math.max(
          0,
          Math.min(totalItemDiscount, itemSubtotal),
        );

        // Amount after ALL discounts (this is the base price after discounts)
        const discountedAmount = itemSubtotal - finalItemDiscount;

        // Calculate tax based on tax type
        let itemTax = 0;
        let itemTotal = 0;
        let netPrice = 0;

        const taxRate = prod.tax_rate || 0;
        const taxType = orgTaxType;

        if (taxRate > 0) {
          if (taxType === "inclusive") {
            // Inclusive tax: the price already includes tax, so extract tax from the discounted amount.
            // discountedAmount is tax-inclusive after discount.
            itemTax = discountedAmount - discountedAmount / (1 + taxRate / 100);
            itemTotal = discountedAmount;
            netPrice = discountedAmount - itemTax;
          } else {
            // Exclusive tax: tax is added on top of the discounted amount.
            itemTax = discountedAmount * (taxRate / 100);
            itemTotal = discountedAmount + itemTax;
            netPrice = discountedAmount;
          }
        } else {
          // No tax
          itemTax = 0;
          itemTotal = discountedAmount;
          netPrice = discountedAmount;
        }

        rawTotalTax += itemTax;
        rawTotalAfterDiscounts += itemTotal;

        return {
          ...prod,
          // Store the final discount amount per item (this goes to API)
          discount: Math.round(finalItemDiscount * 100) / 100, // Round to 2 decimal places
          // Store calculated values for display (not sent to API)
          tax_amount: itemTax,
          total: itemTotal,
          net_price: netPrice,
          base_price: prod.unitPrice ?? prod.base_price,
          tax_type: taxType,
        } as ExtendedSellableItem;
      },
    );

    // Ensure the sum of item discounts matches the effective manual discount (with rounding tolerance)
    const totalItemDiscounts = updatedItemsWithCalculations.reduce(
      (sum, item) => sum + item.discount,
      0,
    );
    const discountDifference = Math.abs(
      totalItemDiscounts - effectiveManualDiscount,
    );

    // If there's a rounding difference, adjust the last item's discount to match
    if (discountDifference > 0.01 && updatedItemsWithCalculations.length > 0) {
      const lastItem =
        updatedItemsWithCalculations[updatedItemsWithCalculations.length - 1];
      const adjustment = effectiveManualDiscount - totalItemDiscounts;
      lastItem.discount = Math.max(
        0,
        Math.min(
          lastItem.discount + adjustment,
          (lastItem as any).itemSubtotal || 0,
        ),
      );

      // Recalculate the last item's totals with adjusted discount
      const lastItemSubtotal = (lastItem as any).itemSubtotal || 0;
      const adjustedDiscountedAmount = lastItemSubtotal - lastItem.discount;
      const lastTaxRate = lastItem.tax_rate || 0;
      const lastTaxType = orgTaxType;

      let lastItemTax = 0;
      let lastItemTotal = 0;
      let lastNetPrice = 0;

      if (lastTaxRate > 0) {
        if (lastTaxType === "inclusive") {
          lastItemTax =
            adjustedDiscountedAmount -
            adjustedDiscountedAmount / (1 + lastTaxRate / 100);
          lastItemTotal = adjustedDiscountedAmount;
          lastNetPrice = adjustedDiscountedAmount - lastItemTax;
        } else {
          lastItemTax = adjustedDiscountedAmount * (lastTaxRate / 100);
          lastItemTotal = adjustedDiscountedAmount + lastItemTax;
          lastNetPrice = adjustedDiscountedAmount;
        }
      } else {
        lastItemTotal = adjustedDiscountedAmount;
        lastNetPrice = adjustedDiscountedAmount;
      }

      // Update the last item's totals
      lastItem.tax_amount = lastItemTax;
      lastItem.total = lastItemTotal;
      lastItem.net_price = lastNetPrice;

      // Recalculate grand totals
      rawTotalTax =
        updatedItemsWithCalculations
          .slice(0, -1)
          .reduce((sum, item) => sum + (item.tax_amount || 0), 0) + lastItemTax;
      rawTotalAfterDiscounts =
        updatedItemsWithCalculations
          .slice(0, -1)
          .reduce((sum, item) => sum + (item.total || 0), 0) + lastItemTotal;
    }

    // Calculate grand total based on tax type
    const isRefund = watcher.transaction_type === SaleStatus.Refund;
    let grandTotal = 0;

    if (isRefund) {
      // For refunds, use the simpler calculation
      grandTotal = rawTotalAfterDiscounts;
    } else {
      // For regular sales, the total should be the sum of all item totals
      // rawTotalAfterDiscounts already contains the correct tax-inclusive totals
      grandTotal = rawTotalAfterDiscounts;
    }

    // For inclusive tax, ensure the total matches what was in the original invoice
    // The grandTotal should be the tax-inclusive total
    const hasInclusiveTax = updatedPayload.some(
      (item: ExtendedSellableItem) => item.tax_type === "inclusive",
    );
    // if (hasInclusiveTax) {
    //   // For inclusive tax, the grandTotal should match the original invoice total
    //   // But since we're recalculating, we need to ensure it matches
    //   console.log("Inclusive tax detected - ensuring totals match original invoice");
    // }

    // console.log("Final Calculations:", {
    //   rawSubtotal,
    //   rawTotalTax,
    //   rawTotalAfterDiscounts,
    //   effectiveManualDiscount,
    //   grandTotal: Math.round(grandTotal)
    // });
    // console.log("=== END SUBTOTAL CALCULATION DEBUG ===");

    // Round and set values
    setValue("sub_total", Math.round(rawSubtotal));
    setValue("tax_amount", Math.round(rawTotalTax));
    setValue("total", Math.round(grandTotal));

    // Filter items to only include simplified structure for API (item_id, item_type, quantity, discount, optional price)
    const simplifiedItems: SellableItem[] = updatedItemsWithCalculations.map(
      (item) => {
        const simplified: SellableItem = {
          item_id: item.item_id,
          item_type: item.item_type,
          quantity: item.quantity,
          discount: item.discount,
          contract_id: item.contract_id,
        };

        // Only include price for wallet products
        const isWalletProduct =
          item.item_type === POSProductType.Product &&
          productListData?.find((p) => p.id === item.item_id)?.type ===
          ProductType.Wallet;

        if (isWalletProduct && item.price !== undefined) {
          simplified.price = item.price;
        }

        // Include booking_date if present (for events/facilities)
        if (item.booking_date) {
          simplified.booking_date = item.booking_date;
        }

        // Include addons if present (for facilities)
        if (item.addons) {
          simplified.addons = item.addons;
        }

        return simplified;
      },
    );

    setValue("items", simplifiedItems);

    if (options?.updatePayload !== false) {
      // Update payload with calculated values but keep tax info for future calculations
      setProductPayload(
        updatedItemsWithCalculations.map((item) => ({
          ...item,
          base_price: item.base_price,
          tax_type: orgTaxType,
        })),
      );
    }
  };

  const [discountError, setDiscountError] = useState("");
  const [isDiscountManuallySet, setIsDiscountManuallySet] = useState(false);

  const totalPrice = watcher.sub_total; // Assuming subtotal is the total price

  const validateDiscount = (value: number) => {
    // Calculate the minimum discount (product discounts)
    const productDiscountTotal = productPayload.reduce(
      (total, product: ExtendedSellableItem) => {
        const discountPercentage = product.discount_percentage || 0;
        const basePrice = product.base_price ?? product.price ?? 0;
        const itemSubtotal = basePrice * product.quantity;
        const itemDiscount = (itemSubtotal * discountPercentage) / 100;
        return total + itemDiscount;
      },
      0,
    );

    // User cannot go below the product discount amount
    if (value < productDiscountTotal) {
      setDiscountError(
        `Discount cannot be less than ${Math.round(productDiscountTotal)} (product discount)`,
      );
      return false;
    }

    // User cannot be more than or equal to subtotal
    if (value >= (totalPrice ?? 0)) {
      setDiscountError("Discount cannot be greater than or equal to subtotal");
      return false;
    }

    setDiscountError("");
    return true;
  };

  const addProduct = async (product: any, type: string) => {
    // REQUIRE MEMBER SELECTION FOR ALL PRODUCT TYPES
    if (!customer) {
      toast({
        variant: "destructive",
        title: "Customer required",
        description: "Please select a customer before adding items to the cart",
      });
      return;
    }

    // Handle facility slots separately
    if (type === "facilities") {
      // Check if slot is already in cart
      const isExistingSlot = productPayload.find(
        (prod) => prod.item_id == product.item_id && prod.item_type === type,
      );

      if (isExistingSlot) {
        toast({
          variant: "destructive",
          title: "Slot already in cart",
          description: "This facility slot is already in your cart.",
        });
        return;
      }

      // Validate member criteria for the facility slot
      const facilitySlotData = (product as ExtendedSellableItem)
        .facility_slot_data;
      if (
        facilitySlotData?.schedule?.general_info?.facility_id &&
        customer?.id
      ) {
        try {
          // Call validation API using lazy query
          const validationResult = await validateCriteria({
            org_id: orgId,
            member_id: customer.id,
            facility_id: facilitySlotData.schedule.general_info.facility_id,
          }).unwrap();

          // Validation passed - continue to add slot
        } catch (error) {
          // Handle validation error
          const typedError = error as ErrorType;
          const errorMessage =
            typedError?.data?.detail ||
            typedError?.data?.message ||
            "Member does not meet facility criteria";

          toast({
            variant: "destructive",
            title: "Validation failed",
            description: errorMessage,
          });
          return; // Don't add the slot
        }
      }

      // Facility slot is already in ExtendedSellableItem format from FacilitySlots component
      const facilitySlotPayload: ExtendedSellableItem =
        product as ExtendedSellableItem;

      // Update the product payload
      setProductPayload((prevPayload) => {
        const updatedPayload = [...prevPayload, facilitySlotPayload];
        updateFormValues(updatedPayload, { clampDiscount: true });
        return updatedPayload;
      });

      return;
    }

    // Handle events separately
    if (type === POSProductType.Event) {
      const event = product as FacilitySchedule;
      const generalInfo = event.general_info;
      const facilityId = generalInfo?.facility_id;

      // Validate member criteria for the facility
      if (facilityId && customer?.id) {
        try {
          // Call validation API using lazy query
          await validateCriteria({
            org_id: orgId,
            member_id: customer.id,
            facility_id: facilityId,
          }).unwrap();

          // Validation passed - continue to add event
        } catch (error) {
          // Handle validation error
          const typedError = error as ErrorType;
          const errorMessage =
            typedError?.data?.detail ||
            typedError?.data?.message ||
            "Member does not meet facility criteria";

          toast({
            variant: "destructive",
            title: "Validation failed",
            description: errorMessage,
          });
          return; // Don't add the event
        }
      }

      // If validation passed or no facility_id, proceed to add event
      let basePrice = generalInfo?.base_price ?? generalInfo?.price ?? 0;
      let eventPrice = basePrice;

      let eventPriceType: any = "base";
      let eventAppliedPricingName: any = "";

      if (generalInfo?.price_master_id) {
        try {
          const pmData = await getPriceMasterById({ id: generalInfo.price_master_id, org_id: Number(orgId) }).unwrap();
          if (pmData) {
            const { price, priceType, appliedPricingName } = calculateItemPrice(basePrice, {
              specialPricing: pmData.special_pricing?.map((sp: any) => ({
                name: sp.name,
                price: sp.price,
                date_from: sp.date_from || "",
                date_to: sp.date_to || "",
              })),
              planPricing: pmData.plan_pricing,
              groupPricing: pmData.group_pricing,
              member: customer,
            });
            eventPrice = price;
            // Store pricing rule info
            eventPriceType = priceType;
            eventAppliedPricingName = priceType === "group" ? pmData.rule_name : appliedPricingName;
          }
        } catch (e) {
          console.error("Failed to fetch price master for event", e);
        }
      }

      // Calculate tax from tax groups (event tax group + member tax group)
      const eventTaxGroup = generalInfo?.tax_group;
      const {
        tax: selectedTax,
        taxRate,
        taxName,
      } = await resolveTax(eventTaxGroup, selectedMemberTaxGroup || null);

      // Construct the simplified event payload
      const newProductPayload: SellableItem = {
        item_id: generalInfo?.id as number,
        item_type: type as POSProductType,
        quantity: 1,
        discount: 0,
        // Price is optional - not included for events
        booking_date: generalInfo?.start_date, // Store event start date
      };
      (newProductPayload as any).price_type = eventPriceType;
      (newProductPayload as any).applied_pricing_name = eventAppliedPricingName;

      // Store tax info for calculations (not in final payload)
      const eventPayloadWithTax: ExtendedSellableItem = {
        ...newProductPayload,
        tax_rate: taxRate,
        tax_type: orgTaxType,
        discount_percentage: 0,
        base_price: eventPrice,
        description: generalInfo?.name || "Unnamed Event",
        tax_name: taxName,
        item_tax_group: eventTaxGroup,
        price_type: (newProductPayload as any).price_type,
        applied_pricing_name: (newProductPayload as any).applied_pricing_name,
      };

      // Update the product payload
      setProductPayload((prevPayload) => {
        const existingProduct = prevPayload.find(
          (prod) => prod.item_id == generalInfo?.id && prod.item_type === type,
        );

        let updatedPayload;
        if (existingProduct) {
          // Check if non-business member is trying to increment beyond quantity 1
          if (
            customer &&
            !customer.is_business &&
            existingProduct.quantity >= 1
          ) {
            toast({
              variant: "destructive",
              title: "Quantity limit reached",
              description:
                "Non-business members can only purchase 1 ticket per event",
            });
            return prevPayload; // Return unchanged if limit would be exceeded
          }

          // Increment quantity for existing event
          updatedPayload = prevPayload.map((prod) =>
            prod.item_id == generalInfo?.id && prod.item_type === type
              ? {
                ...prod,
                quantity: prod.quantity + 1,
              }
              : prod,
          );
        } else {
          // New event - always allow adding (validation already passed or no facility_id)
          updatedPayload = [...prevPayload, eventPayloadWithTax];
        }

        updateFormValues(updatedPayload, { clampDiscount: true });
        return updatedPayload;
      });

      return;
    }

    // Handle Products and Memberships
    // Check if this is any wallet product
    const isWalletProduct =
      type === POSProductType.Product && product.type === ProductType.Wallet;

    if (isWalletProduct) {
      // Calculate current total of all wallet products in cart
      const currentWalletTotal = productPayload.reduce((total, item) => {
        const isExistingWalletProduct =
          item.item_type === POSProductType.Product &&
          productListData?.find((p) => p.id === item.item_id)?.type ===
          ProductType.Wallet;

        return isExistingWalletProduct
          ? total + (item.price || 0) * item.quantity // Use price * quantity for validation as this is before tax
          : total;
      }, 0);

      // Check if adding this product would exceed the limit
      if (
        currentWalletTotal + (product.net_price || product.price || 0) >
        100000
      ) {
        toast({
          variant: "destructive",
          title: "Wallet limit exceeded",
          description: "Total wallet amount cannot exceed 1 lac (100,000)",
        });
        return; // Don't add the product if it would exceed the limit
      }

      // Individual product price check (keep existing check)
      if ((product.net_price || product.price || 0) > 100000) {
        toast({
          variant: "destructive",
          title: "Price limit exceeded",
          description: "Wallet amount cannot exceed 1 lac (100,000)",
        });
        product.net_price = 100000;
      }
    }

    // Inventory stock check
    if (type === POSProductType.Product) {
      const productInList = productListData?.find((p) => p.id === product.id);
      const existingProduct = productPayload.find(
        (prod) => prod.item_id == product.id && prod.item_type == type,
      );
      const currentCartQty = existingProduct?.quantity || 0;

      if (
        productInList?.manage_inventory &&
        productInList?.quantity !== undefined &&
        currentCartQty + 1 > productInList.quantity
      ) {
        toast({
          variant: "destructive",
          title: "Stock limit reached",
          description: `Only ${productInList.quantity} unit(s) available in stock`,
        });
        return;
      }
    }

    // Reset discount error when adding products
    setDiscountError("");

    // Reset manual discount flag when adding products
    setIsDiscountManuallySet(false);

    // Calculate tax from tax groups
    const productTaxGroup = product.tax_group;
    const {
      tax: selectedTax,
      taxRate,
      taxName,
    } = await resolveTax(productTaxGroup, selectedMemberTaxGroup || null);

    // For flexible price products, we'll allow editing the price later
    const isFlexiblePrice =
      type === POSProductType.Product &&
      product.type === ProductType.Wallet &&
      product.flexible_price;

    // Calculate discount percentage (memberships have percentage discount)
    const discountPercentage =
      type === POSProductType.Membership
        ? product.discount || 0 // Membership discount is already a percentage
        : isFlexiblePrice
          ? 0
          : product.discount || 0; // Product discount is also percentage

    let basePrice = product.base_price ?? product.net_price ?? product.price ?? 0;
    if (isFlexiblePrice) basePrice = 0;

    let resolvedPriceType: any = "base";
    let resolvedAppliedPricingName: any = "";

    // Apply exact lowest price according to computing rules if the product has a price_master_id
    if (!isFlexiblePrice && product.price_master_id && type !== POSProductType.Event) {
      try {
        const pmData = await getPriceMasterById({ id: product.price_master_id, org_id: Number(orgId) }).unwrap();
        if (pmData) {
          const { price: bestPrice, priceType, appliedPricingName } = calculateItemPrice(basePrice, {
            specialPricing: pmData.special_pricing?.map((sp: any) => ({
              name: sp.name,
              price: sp.price,
              date_from: sp.date_from || "",
              date_to: sp.date_to || "",
            })),
            planPricing: pmData.plan_pricing,
            groupPricing: pmData.group_pricing,
            member: customer,
          });
          basePrice = bestPrice;
          // Store pricing rule info
          resolvedPriceType = priceType;
          resolvedAppliedPricingName = priceType === "group" ? pmData.rule_name : appliedPricingName;
        }
      } catch (e) {
        console.error("Failed to fetch price master for product", e);
      }
    }

    // Construct the simplified product payload
    const newProductPayload: SellableItem = {
      item_id: product.id,
      item_type: type as POSProductType,
      quantity: 1,
      discount: 0, // Will be calculated in updateFormValues
      // Price is optional - only for wallet products
      ...(isWalletProduct && { price: basePrice }),
    };
    (newProductPayload as any).price_type = resolvedPriceType;
    (newProductPayload as any).applied_pricing_name = resolvedAppliedPricingName;

    // Store tax info for calculations (not in final payload)
    const productPayloadWithTax: ExtendedSellableItem = {
      ...newProductPayload,
      tax_rate: taxRate,
      tax_type: orgTaxType,
      discount_percentage: discountPercentage,
      base_price: basePrice,
      description:
        product.name || product.product_name || product.membership_name,
      tax_name: taxName,
      item_tax_group: productTaxGroup,
      price_type: (newProductPayload as any).price_type,
      applied_pricing_name: (newProductPayload as any).applied_pricing_name,
    };

    // Update the product payload to ensure unique entries and recalculate totals
    setProductPayload((prevPayload) => {
      const existingProduct = prevPayload.find(
        (prod) => prod.item_id == product.id && prod.item_type == type,
      );

      let updatedPayload;
      if (existingProduct) {
        // Update existing product in payload - only update quantity, let updateFormValues handle calculations
        updatedPayload = prevPayload.map((prod) =>
          prod.item_id == product.id && prod.item_type === type
            ? {
              ...prod,
              quantity: prod.quantity + 1,
              // Keep other values as they are, updateFormValues will recalculate
            }
            : prod,
        );
      } else {
        // Add new product to payload with tax info
        updatedPayload = [...prevPayload, productPayloadWithTax];
      }

      updateFormValues(updatedPayload, { clampDiscount: true });

      return updatedPayload;
    });
  };

  const updateProductQuantity = (
    productId: number,
    itemType: POSProductType,
    action: "increment" | "decrement",
  ) => {
    setProductPayload((prevPayload) => {
      // Find the product before processing
      const currentProduct = prevPayload.find(
        (prod) => prod.item_id === productId && prod.item_type === itemType,
      );

      // Check if this is a wallet product and if incrementing would exceed 1 lac
      if (action === "increment" && currentProduct) {
        const isWalletProduct =
          currentProduct.item_type === POSProductType.Product &&
          productListData?.find((p) => p.id === currentProduct.item_id)
            ?.type === ProductType.Wallet;

        if (isWalletProduct) {
          // Check individual product total (using base price for validation)
          const basePrice =
            (currentProduct as ExtendedSellableItem).base_price ??
            currentProduct.price ??
            0;
          const singleProductNewTotal =
            basePrice * (currentProduct.quantity + 1);

          // Calculate total of ALL wallet products (excluding the current one)
          const otherWalletProductsTotal = prevPayload.reduce((total, item) => {
            if (item.item_id === currentProduct.item_id) return total; // Skip current product

            const isWalletProduct =
              item.item_type === POSProductType.Product &&
              productListData?.find((p) => p.id === item.item_id)?.type ===
              ProductType.Wallet;

            const itemBasePrice =
              (item as ExtendedSellableItem).base_price ?? item.price ?? 0;
            return isWalletProduct
              ? total + itemBasePrice * item.quantity
              : total; // Use price * quantity for validation
          }, 0);

          // Check combined total
          if (singleProductNewTotal + otherWalletProductsTotal > 100000) {
            toast({
              variant: "destructive",
              title: "Wallet limit exceeded",
              description:
                "Total wallet amount across all wallet products cannot exceed 1 lac (100,000)",
            });
            return prevPayload; // Return unchanged if limit would be exceeded
          }

          // Keep the individual product check as well
          if (singleProductNewTotal > 100000) {
            toast({
              variant: "destructive",
              title: "Quantity limit reached",
              description: "Total wallet amount cannot exceed 1 lac (100,000)",
            });
            return prevPayload;
          }
        }

        // Inventory stock check for products
        if (currentProduct.item_type === POSProductType.Product && action === "increment") {
          const productInList = productListData?.find(
            (p) => p.id === currentProduct.item_id,
          );
          if (
            productInList?.manage_inventory &&
            productInList?.quantity !== undefined &&
            currentProduct.quantity + 1 > productInList.quantity
          ) {
            toast({
              variant: "destructive",
              title: "Stock limit reached",
              description: `Only ${productInList.quantity} unit(s) available in stock`,
            });
            return prevPayload;
          }
        }

        // Validation: Non-business members can only have quantity 1 for events
        const isEvent = currentProduct.item_type === POSProductType.Event;
        if (
          isEvent &&
          customer &&
          !customer.is_business &&
          currentProduct.quantity >= 1
        ) {
          toast({
            variant: "destructive",
            title: "Quantity limit reached",
            description: `Non-business members can only purchase 1 event per booking`,
          });
          return prevPayload; // Return unchanged if limit would be exceeded
        }
      }

      // Original implementation continues
      const newPayload = prevPayload
        .map((product) => {
          if (product.item_id === productId && product.item_type === itemType) {
            // New quantity based on action
            const newQuantity =
              action === "increment"
                ? product.quantity + 1
                : product.quantity - 1;

            // Remove product if quantity reaches zero
            if (newQuantity <= 0) return null;

            // Only update quantity, let updateFormValues handle all calculations
            return {
              ...product,
              quantity: newQuantity,
              // Keep other values as they are, updateFormValues will recalculate everything
            };
          }
          return product;
        })
        .filter((product) => product !== null) as typeof prevPayload;

      // If decrementing, adjust the overall manual discount by the item's per-unit product discount
      if (action === "decrement") {
        const decrementedProduct = prevPayload.find(
          (p) => p.item_id === productId && p.item_type === itemType,
        ) as ExtendedSellableItem | undefined;
        if (decrementedProduct) {
          const perUnitSubtotal =
            decrementedProduct.base_price ?? decrementedProduct.price ?? 0; // base_price reflects per-unit base
          const perUnitProductDiscount =
            ((decrementedProduct.discount_percentage || 0) * perUnitSubtotal) /
            100;

          // Compute new product-discount total and subtotal based on the newPayload
          const recalculatedSubtotal = newPayload.reduce(
            (sum, it: ExtendedSellableItem) => {
              const basePrice = it.base_price ?? it.price ?? 0;
              return sum + basePrice * it.quantity;
            },
            0,
          );
          const recalculatedProductDiscount = newPayload.reduce(
            (sum, it: ExtendedSellableItem) => {
              const perc = it.discount_percentage || 0;
              const basePrice = it.base_price ?? it.price ?? 0;
              return sum + (basePrice * it.quantity * perc) / 100;
            },
            0,
          );

          const minAllowed = Math.round(recalculatedProductDiscount);
          const maxAllowed = Math.max(0, Math.round(recalculatedSubtotal) - 1);
          const currentManual = Number(watcher.discount || 0);

          // Reduce current manual discount by the removed unit's product discount
          const proposed = currentManual - Math.round(perUnitProductDiscount);
          const clamped = Math.min(Math.max(proposed, minAllowed), maxAllowed);
          if (clamped !== currentManual) {
            setValue("discount", clamped, { shouldValidate: false });
          }
        }
      }

      // Reset values if payload is empty
      if (newPayload.length === 0) {
        setValue("discount", 0);
        setValue("sub_total", 0);
        setValue("tax_amount", 0);
        setValue("total", 0);
        setIsDiscountManuallySet(false);
      }

      updateFormValues(newPayload, { clampDiscount: true });
      setDiscountError("");

      return newPayload;
    });
  };

  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const prevCustomerRef = useRef<MemberTableDatatypes | null>(null); // Ref to store previous customer

  // Add this function to fetch and display token balance when a customer is selected
  const fetchAndDisplayBalance = useCallback(
    async (walletAddress: string | null | undefined) => {
      if (!walletAddress) {
        setTokenBalance(null);
        return;
      }

      try {
        setLoadingBalance(true);
        const balanceData = await getTokenBalance({
          userAddress: walletAddress,
          orgId: orgId,
        });
        setTokenBalance(balanceData.balance);
      } catch (error) {
        console.error("Failed to retrieve token balance:", error);
        setTokenBalance(null);
        toast({
          variant: "destructive",
          title: "Failed to retrieve token balance",
          description: error instanceof Error ? error.message : "Unknown error",
        });
      } finally {
        setLoadingBalance(false);
      }
    },
    [orgId],
  );
  const discountAmt =
    useWatch({ control: form.control, name: "discount", defaultValue: 0 }) || 0;

  useEffect(() => {
    if (customer) {
      setValue("is_business", customer.is_business as boolean);
      setValue("member_id", customer.id);
      setValue("member_name", customer.first_name + " " + customer.last_name);
      setValue("member_email", customer.email);
      setValue("member_address", customer.address_1);
      setValue("wallet_address", customer.wallet_address);
      setValue("member_gender", customer.gender);
      setValue("member_nic", customer.nic);

      // Fetch token balance when customer is selected and has a wallet address
      if (customer.wallet_address) {
        fetchAndDisplayBalance(customer?.wallet_address);
      } else {
        setTokenBalance(null);
      }
    } else if (!invoice) {
      // When customer is unselected, also clear customer-related form fields
      setValue("is_business", false);
      setValue("member_id", null);
      setValue("member_name", null);
      setValue("member_email", null);
      setValue("member_address", null);
      setValue("wallet_address", null);
      setValue("member_gender", null);
      setValue("member_nic", undefined);
      setTokenBalance(null);
    }
  }, [customer, fetchAndDisplayBalance, setValue]);

  // New effect for clearing cart on customer change or deselection
  useEffect(() => {
    // Check if customer has actually changed (including deselection)
    const customerChanged = prevCustomerRef.current?.id !== customer?.id;
    const customerDeselected =
      prevCustomerRef.current !== null && customer === null;
    const customerReselected =
      prevCustomerRef.current === null && customer !== null;

    // Only trigger if:
    // 1. Customer has changed (different ID or deselected/reselected)
    // 2. There are items in the cart
    // 3. We're not currently loading data programmatically
    if (
      (customerChanged || customerDeselected || customerReselected) &&
      productPayload.length > 0 &&
      !isLoadingDataRef.current
    ) {
      setProductPayload([]); // Clear product payload
      setValue("items", []); // Clear form items
      setValue("discount", 0); // Reset discount
      setValue("sub_total", 0); // Reset sub_total
      setValue("tax_amount", 0); // Reset tax_amount
      setValue("total", 0); // Reset total
      setIsDiscountManuallySet(false);

      if (customerDeselected) {
        toast({
          variant: "destructive",
          title: "Customer deselected",
          description: "Cart has been cleared as customer was deselected.",
        });
      } else if (customerChanged) {
        toast({
          variant: "destructive",
          title: "Customer changed",
          description:
            "Cart has been cleared as customer was changed. Taxes may differ for the new customer.",
        });
      }
    }
    // Always update the ref with the *current* customer for the *next* render's comparison
    prevCustomerRef.current = customer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]); // Only depend on customer ID to prevent unnecessary runs

  // Separate effect for calculating totals when payload or discount changes
  useEffect(() => {
    // Only run recalculation if there are items or if the discount/manual discount is being adjusted
    // And importantly, only if the payload isn't empty (or has just been made empty by a clear operation)
    if (productPayload.length > 0) {
      updateFormValues(productPayload, {
        clampDiscount: false,
        updatePayload: false,
      });
    } else {
      // If the productPayload is empty, ensure all relevant form values are reset to 0.
      // This handles cases where the cart is explicitly emptied.
      setValue("discount", 0);
      setValue("sub_total", 0);
      setValue("tax_amount", 0);
      setValue("total", 0);
      setIsDiscountManuallySet(false);
    }
  }, [productPayload, discountAmt, isDiscountManuallySet]); // Removed setValue as it's a stable function from react-hook-form

  // Additional effect to handle discount validation
  useEffect(() => {
    if (discountAmt !== undefined && productPayload.length > 0) {
      // Validate without toasting on each keystroke to avoid input jitter
      validateDiscount(discountAmt);
    }
  }, [discountAmt, productPayload]);

  const handleCloseDayExceeded = () => {
    setDayExceeded(false);
    navigate(`/admin/pos/register`);
  };

  const handleContinueDayExceeded = () => {
    localStorage.setItem("isContinue", "true");
    setDayExceeded(false);
  };

  const handleGetWallet = async () => {
    try {
      const wallet = await getWallet(orgId); // Replace 1 with the actual ID
      console.log("Wallet Data:", wallet);
    } catch (error) {
      console.error("Error fetching wallet:", error);
    }
  };

  const validateFlexiblePricing = () => {
    // Check if any flexible price product has a price of 0
    const invalidFlexibleProducts = productPayload.filter(
      (product: ExtendedSellableItem) =>
        product.item_type === POSProductType.Product &&
        productListData?.find((p) => p.id === product.item_id)
          ?.flexible_price &&
        (product.price === 0 || !product.price),
    );

    if (invalidFlexibleProducts.length > 0) {
      toast({
        variant: "destructive",
        title: "Price required",
        description: `Please enter a price for ${invalidFlexibleProducts.map((p: ExtendedSellableItem) => p.description || "item").join(", ")} before proceeding to checkout.`,
      });
      return false;
    }

    return true;
  };

  const validateCheckout = () => {
    if (!customer) {
      toast({
        variant: "destructive",
        title: "Please select a customer",
      });
      return false;
    }

    // Add this line to validate flexible pricing
    if (!validateFlexiblePricing()) {
      return false;
    }

    // Check if there's a wallet product in the cart but customer has no wallet address
    const hasWalletProduct = productPayload.some(
      (product) =>
        product.item_type === POSProductType.Product &&
        productListData?.find((p) => p.id === product.item_id)?.type ===
        ProductType.Wallet,
    );

    // Check if there's a facility slot in the cart
    const hasFacilitySlot = productPayload.some(
      (product) => product.item_type === "facilities",
    );

    if (
      hasWalletProduct &&
      (!customer?.wallet_address || customer.wallet_address === null)
    ) {
      toast({
        variant: "destructive",
        title: "Cannot top up wallet as member has no wallet",
      });
      return false;
    }

    if (hasWalletProduct) {
      // First check if wallet address exists
      if (!customer?.wallet_address || customer.wallet_address === null) {
        toast({
          variant: "destructive",
          title: "Cannot top up wallet as member has no wallet",
        });
        return false;
      }

      // Then validate if it's a proper Ethereum address
      const isValidEthAddress = /^0x[a-fA-F0-9]{40}$/.test(
        customer.wallet_address,
      );
      const isValidAddress = isAddress(customer.wallet_address, {
        strict: true,
      });
      if (!isValidEthAddress) {
        toast({
          variant: "destructive",
          description: "Customer's wallet address is not valid",
        });
        return false;
      }
    }

    if (productPayload.length == 0) {
      toast({
        variant: "destructive",
        title:
          "Please add at least one item to the receipt to enable checkout.",
      });
      return false;
    }

    // For refunds, discount validation is different since discount is applied at item level
    if (
      watcher.transaction_type !== SaleStatus.Refund &&
      (watcher.discount as number) >= (watcher.sub_total as number)
    ) {
      toast({
        variant: "destructive",
        title: "Discount should be less than subtotal amount",
      });
      return false;
    }
    setDiscountError("");
    return true;
  };

  const paymentCheckout = () => {
    const validation = validateCheckout();
    validation && setShowCheckout(true);
    setDiscountError("");
  };

  const parkSale = async () => {
    const validation = validateCheckout();
    if (!validation) {
      return;
    }
    const payload = {
      ...watcher,
      status: ProductStatus.Pending,
      submitted_to_authority: false,
    };
    try {
      if (!payload.id) {
        const resp = await createTransaction(payload).unwrap();
        if (resp) {
          toast({
            variant: "success",
            title: "Sale parked successfully",
          });
        }
      } else if (payload.id) {
        const resp = await updateTransaction(payload).unwrap();
        if (resp) {
          toast({
            variant: "success",
            title: "Sale parked updated successfully",
          });
        }
      }

      // Clear ALL state FIRST before resetting form
      setProductPayload([]);
      setDiscountError("");
      setIsDiscountManuallySet(false);
      setCustomer(null);
      setTransactionId(undefined);

      // Use setTimeout to ensure state updates are processed before reset
      setTimeout(() => {
        reset(initialValues as Transaction);
        transactionRefetch();

        if (id) {
          navigate("/admin/pos/sell/");
        }
      }, 0);
    } catch (error: unknown) {
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  useEffect(() => {
    if (Number(id)) {
      setTransactionId(Number(id));
      setDiscountError("");
    }
  }, [id]);

  useEffect(() => {
    // for retrive sale, payment from sale history (pending to paid), refund
    if (retriveSaleData) {
      isLoadingDataRef.current = true; // Mark that we're loading data programmatically
      console.log({ retriveSaleData }, "inside hrere");
      // transactionId is for refund (remove tax in refund)

      let refundItemsWithoutTax = retriveSaleData.items;

      if (Number(id)) {
        refundItemsWithoutTax = retriveSaleData.items.map((item: any) => ({
          ...item,
          tax_amount: 0,
          tax_rate: 0,
          // Preserve the original discount values for proper calculation
          discount: item.discount || 0,
          discount_percentage: item.discount_percentage || 0,
        }));
      }

      const payload = {
        ...retriveSaleData,
        staff_id: userInfo?.user.id,
        counter_id: counter_number as number, //counter id
        staff_name: userInfo?.user.first_name,
        batch_id: lastSession?.id, //register id
        items: refundItemsWithoutTax, //items without tax
      };

      // for refund
      if (Number(id) && payload.status === "Paid") {
        delete payload.id;
        payload.transaction_type = SaleStatus.Refund;
        payload.amount_received = 0;
        payload.change_amount = 0;
        payload.receipt_number =
          other_settings?.invoice_prefix + Math.floor(Math.random() * 99);
        payload.main_transaction_id = Number(id);
        payload.payments = [];

        const initialMaxQuantities = payload.items.reduce(
          (acc, item) => {
            acc[item.item_id] = item.quantity;
            return acc;
          },
          {} as Record<number, number>,
        );
        setMaxProductsQauntity(initialMaxQuantities);
      }
      // Ensure items have the discount_percentage property
      let itemsWithDiscountPercentage = refundItemsWithoutTax;
      if (Number(id)) {
        itemsWithDiscountPercentage = refundItemsWithoutTax.map((item: any) => {
          let processedItem = {
            ...item,
            discount_percentage: item.discount || 0,
          };

          // Handle inclusive vs exclusive tax types properly for refunds/retrievals
          if (item.tax_type === "inclusive") {
            // For inclusive tax: use total as tax-inclusive price
            processedItem.price = item.total || item.price;
            processedItem.net_price =
              item.sub_total || item.net_price || item.price;
          } else {
            // For exclusive tax: use price as base price
            processedItem.price = item.price || item.sub_total;
            processedItem.net_price =
              item.net_price || item.price || item.sub_total;
          }

          return processedItem;
        });
      }

      // Update the payload with normalized items
      payload.items = itemsWithDiscountPercentage;

      // Recompute subtotal and product-discount minimum from items
      // For inclusive tax, we need to extract base prices for calculations
      const recalculatedSubtotal = itemsWithDiscountPercentage.reduce(
        (sum, it: any) => {
          let basePrice = it.price;
          if (it.tax_type === "inclusive" && it.tax_rate > 0) {
            basePrice = it.price / (1 + it.tax_rate / 100);
          }
          return sum + basePrice * it.quantity;
        },
        0,
      );

      const recalculatedProductDiscount = itemsWithDiscountPercentage.reduce(
        (sum, it: any) => {
          const perc = it.discount_percentage || 0;
          let basePrice = it.price;
          if (it.tax_type === "inclusive" && it.tax_rate > 0) {
            basePrice = it.price / (1 + it.tax_rate / 100);
          }
          return sum + (basePrice * it.quantity * perc) / 100;
        },
        0,
      );

      const minAllowedDiscount = Math.round(recalculatedProductDiscount);
      const maxAllowedDiscount = Math.max(
        0,
        Math.round(recalculatedSubtotal) - 1,
      );
      const currentManual = Number(retriveSaleData.discount || 0);
      const clampedManual = Math.min(
        Math.max(currentManual, minAllowedDiscount),
        maxAllowedDiscount,
      );

      // For inclusive tax, use the original values to avoid calculation issues
      if (
        retriveSaleData.items?.some(
          (item: any) => item.tax_type === "inclusive",
        )
      ) {
        payload.sub_total = Math.round(Number(retriveSaleData.sub_total));
        payload.tax_amount = Math.round(Number(retriveSaleData.tax_amount));
        payload.total = Math.round(retriveSaleData.total);
      } else {
        payload.sub_total = Math.round(recalculatedSubtotal);
        payload.total = Math.round(retriveSaleData.total); // Keep original total
      }
      payload.discount = clampedManual;

      console.log("refund", { payload });
      reset(payload, { keepDirtyValues: true });

      if (memberListData && memberListData?.length > 0) {
        setCustomer(
          memberListData?.find(
            (member) => member.id == payload.member_id,
          ) as MemberTableDatatypes,
        );
      }

      // Convert to ExtendedSellableItem format
      const extendedItems: ExtendedSellableItem[] =
        itemsWithDiscountPercentage.map((item: any) => ({
          ...item,
          base_price: item.price || item.base_price,
          description: item.description || "Item",
          tax_rate: item.tax_rate || 0,
          tax_type: item.tax_type || "exclusive",
          discount_percentage: item.discount_percentage || 0,
          // For retrive sale, preserve the original discount amount
          discount: item.discount || 0,
        }));

      setProductPayload(extendedItems);

      // Trigger calculation after state has been set
      setTimeout(() => {
        updateFormValues(extendedItems, { clampDiscount: true });
      }, 0);
      setTimeout(() => {
        updateFormValues(extendedItems, { clampDiscount: true });
      }, 0);

      // open checkout if status is set to refunded
      if (Number(id) && payload.transaction_type === SaleStatus.Refund) {
        setShowCheckout(true);
      }

      // Reset loading flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isLoadingDataRef.current = false;
      }, 100);
    }
  }, [retriveSaleData, memberListData]);

  console.log({ watcher, errors, productPayload, transactionId, customer });

  const handleFlexiblePriceChange = (
    productId: number,
    newPriceStr: string,
  ) => {
    // Handle empty input separately
    if (newPriceStr === "") {
      // Existing empty input handling code
      return;
    }

    const newPrice = parseFloat(newPriceStr);
    if (isNaN(newPrice) || newPrice < 0) return;

    // Check if price exceeds 1 lac (100,000)
    if (newPrice > 100000) {
      toast({
        variant: "destructive",
        title: "Price limit exceeded",
        description: "Wallet amount cannot exceed 1 lac (100,000)",
      });

      // IMPORTANT: Reset the input to the max allowed value instead of returning
      setProductPayload((prevPayload) => {
        const newPayload = prevPayload.map((product: ExtendedSellableItem) => {
          if (product.item_id === productId) {
            // Use maximum allowed value instead
            const maxAllowedPrice = 100000;

            // Update both price and base_price for flexible price products
            return {
              ...product,
              price: maxAllowedPrice,
              base_price: maxAllowedPrice,
              // Keep other values as they are, updateFormValues will recalculate everything
            };
          }
          return product;
        });

        updateFormValues(newPayload, { clampDiscount: true });
        return newPayload;
      });
      return;
    }

    // Existing code for normal price updates
    setProductPayload((prevPayload) => {
      const newPayload = prevPayload.map((product: ExtendedSellableItem) => {
        if (product.item_id === productId) {
          // Update both price and base_price for flexible price products
          return {
            ...product,
            price: newPrice,
            base_price: newPrice,
            // Keep other values as they are, updateFormValues will recalculate everything
          };
        }
        return product;
      });

      updateFormValues(newPayload, { clampDiscount: true });
      return newPayload;
    });
  };
  return (
    <div>
      <FormProvider {...form}>
        <div className="p-5">
          {!showCheckout && (
            <Card className=" h-fit px-3 py-4 max-w-[1100px] mx-auto">
              <div className="grid grid-cols-1  slg:grid-cols-2 justify-start items-start gap-3">
                <div className="min-h-36  p-2">
                  <FloatingLabelInput
                    id="search"
                    placeholder="Search"
                    onChange={(event) => setInputValue(event.target.value)}
                    className="w-full pl-8 text-gray-400 rounded-sm"
                    icon={
                      <Search className="size-4 text-gray-400 absolute  z-10 left-2" />
                    }
                  />

                  <Tabs
                    value={activeTab}
                    onValueChange={(value) =>
                      setActiveTab(value as POSProductType)
                    }
                    defaultValue={POSProductType.Membership}
                    className="w-full mt-4 "
                  >
                    <TabsList
                      variant="underline"
                      className="w-full justify-start gap-4 overflow-x-auto overflow-y-hidden whitespace-nowrap flex-nowrap shrink-0 max-w-[100vw] sm:max-w-full"
                    >
                      {productCategories.map((category) => (
                        <TabsTrigger
                          key={category.type}
                          value={category.type}
                          variant="underline"
                        >
                          {category.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {productCategories.map((category) => (
                      <TabsContent
                        className="m-0 w-full"
                        key={category.type}
                        value={category.type}
                      >
                        {category.type === POSProductType.Product ? (
                          <div className="mt-4 w-full space-y-4">
                            {/* <Tabs
                              value={productTypeFilter}
                              onValueChange={(value) =>
                                setProductTypeFilter(value as ProductType)
                              }
                            >
                              <TabsList
                                variant="underline"
                                className="w-full justify-start gap-4 overflow-x-auto"
                              >
                                {productTypeTabs.map((tab) => (
                                  <TabsTrigger
                                    key={tab.value}
                                    value={tab.value}
                                    variant="underline"
                                  >
                                    {tab.label}
                                  </TabsTrigger>
                                ))}
                              </TabsList>
                            </Tabs> */}
                            {category.products.length > 0 ? (
                              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-4">
                                {(category.products as Product[]).map(
                                  (prod) => {
                                    const derivedPrice = prod.flexible_price ? 0 : (prod.base_price ?? prod.price ?? 0);
                                    return (
                                      <ProductCard
                                        key={prod.id}
                                        item={
                                          {
                                            item_id: prod?.id as number,
                                            item_type: POSProductType.Product,
                                            quantity: prod.quantity ?? 0,
                                            discount: 0,
                                            price: derivedPrice,
                                            // Extended properties for ProductCard display
                                            name: prod.product_name,
                                            net_price: derivedPrice,
                                            image_url: prod.image_url,
                                            manage_inventory:
                                              prod.manage_inventory,
                                            flexible_price: prod.flexible_price,
                                            type: prod.type,
                                          } as any
                                        }
                                        categoryType={POSProductType.Product}
                                        disabled={Boolean(
                                          invoice ||
                                          (id &&
                                            watcher.transaction_type ==
                                            SaleStatus.Refund) ||
                                          (prod.manage_inventory && (prod.quantity ?? 0) <= (productPayload.find(p => p.item_id === prod.id && p.item_type === POSProductType.Product)?.quantity || 0))
                                        )}
                                        onSelect={() =>
                                          addProduct(prod, category.type)
                                        }
                                      />
                                    );
                                  },
                                )}
                              </div>
                            ) : (
                              <p className="text-center text-sm text-muted-foreground">
                                {category.empty}
                              </p>
                            )}
                          </div>
                        ) : category.type === POSProductType.Event &&
                          category.products.length > 0 ? (
                          <div className="mt-4 w-full grid grid-cols-2 gap-3 md:grid-cols-3 ">
                            {category.products.map(
                              (event: FacilitySchedule, i: number) => (
                                <EventCard
                                  key={event.general_info?.id || i}
                                  event={event}
                                  customer={customer}

                                  disabled={Boolean(
                                    invoice ||
                                    (id &&
                                      watcher.transaction_type ==
                                      SaleStatus.Refund),
                                  )}
                                  onSelect={() =>
                                    addProduct(event, category?.type)
                                  }
                                />
                              ),
                            )}
                          </div>
                        ) : (category.type === POSProductType.Membership || category.type === POSProductType.CreditAddOns) &&
                          category.products.length > 0 ? (
                          <div className="mt-4 w-full grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-4">
                            {category.products.map((member: Membership) => {
                              const derivedPrice = member.base_price ?? member.net_price ?? 0;
                              return (
                                <ProductCard
                                  key={member.id}
                                  item={
                                    {
                                      item_id: member.id,
                                      item_type: POSProductType.Membership,
                                      quantity: 1,
                                      discount: 0,
                                      // Extended properties for ProductCard display
                                      name: member.name || (member as any).membership_name,
                                      net_price: derivedPrice,
                                    } as any
                                  }
                                  categoryType={POSProductType.Membership}
                                  disabled={Boolean(
                                    invoice ||
                                    (id &&
                                      watcher.transaction_type ==
                                      SaleStatus.Refund),
                                  )}
                                  onSelect={() =>
                                    addProduct(member, category.type)
                                  }
                                />
                              );
                            })}
                          </div>
                        ) : category.type === "facilities" ? (
                          <div className="mt-4 w-full">
                            <FacilitySlots
                              orgId={orgId}
                              orgTaxType={orgTaxType}
                              selectedMember={customer}
                              selectedMemberTaxGroup={selectedMemberTaxGroup}
                              taxGroups={taxGroupsData?.data}
                              cartedSlotIds={productPayload
                                .filter((p) => p.item_type === "facilities")
                                .map((p) => p.item_id)}
                              onAddToCart={(slotData) => {
                                // Convert facility slot data to POS format and add to cart
                                // Convert facility slot data to POS format (simplified structure)
                                const facilitySlotItem: ExtendedSellableItem = {
                                  item_id: slotData.slot.id,
                                  item_type: "facilities" as POSProductType,
                                  quantity: 1,
                                  discount: 0,
                                  addons: slotData.selectedAddOns.map(
                                    (addon: {
                                      id: number;
                                      name: string;
                                      price: number;
                                      type: string;
                                      quantity: number;
                                    }): { id: number; quantity: number; price: number } => ({
                                      id: addon.id,
                                      quantity: addon.quantity,
                                      price: addon.price
                                    })
                                  ), // Include add-on IDs
                                  booking_date: slotData.slot.slot_date, // Include booking date
                                  // Store tax info for calculations
                                  tax_rate: slotData.taxRate,
                                  tax_type: orgTaxType, // Use org tax type
                                  discount_percentage: 0,
                                  base_price: slotData.subtotal, // Use subtotal (exclusive price) as base_price
                                  description: `${slotData.schedule.general_info?.facility_name} - ${displayDate(slotData.slot.slot_date)} ${slotData.slot.start_time}-${slotData.slot.end_time}`,
                                  // Store facility slot data for reference (not sent to API)
                                  facility_slot_data: slotData as any,
                                  tax_name: slotData.taxName, // Store tax name for display
                                  item_tax_group: slotData.schedule.general_info?.tax_group,
                                  price_type: slotData.priceType,
                                  applied_pricing_name: slotData.appliedPricingName,
                                };

                                setProductPayload((prevPayload) => {
                                  const existingItem = prevPayload.find(
                                    (item) => item.item_id === facilitySlotItem.item_id && item.item_type === "facilities"
                                  );

                                  if (existingItem) {
                                    return prevPayload;
                                  }

                                  const updatedPayload = [...prevPayload, facilitySlotItem];
                                  updateFormValues(updatedPayload, { clampDiscount: true });
                                  return updatedPayload;
                                });
                              }}
                            />
                          </div>
                        ) : (
                          <p className="col-span-2 text-sm text-center w-full p-2 mt-2">
                            {category.empty}
                          </p>
                        )}
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>

                {/* checkout summary */}
                <div className="h-full flex flex-col justify-start space-y-2 rounded-sm bg-gray-100 p-2">
                  <div className="item-center flex gap-3">
                    <RetriveSaleCombobox
                      label={"Retrive Sale"}
                      list={transactiontableData}
                      setCustomer={setCustomer}
                      customer={customer}
                      customerList={memberListData}
                      setTransactionId={setTransactionId}
                      setWatcherValue={setValue}
                      setProductPayload={setProductPayload}
                      refetch={retriveSaleRefetch}
                      reset={reset}
                      isLoadingDataRef={isLoadingDataRef}
                      setSelectedMemberTaxGroup={setSelectedMemberTaxGroup}
                      memberTaxGroups={memberTaxGroups}
                    />
                    <Button
                      onClick={() => {
                        const validation = validateCheckout();
                        validation && setParkSale(true);
                      }}
                      disabled={
                        id && watcher.transaction_type == SaleStatus.Refund
                          ? true
                          : false
                      }
                      className="w-full justify-center items-center gap-2"
                    >
                      <i className="fa-regular fa-clock"></i>
                      Park Sale
                    </Button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1">
                      <CustomerCombobox
                        label={"customer"}
                        className="bg-white"
                        customerList={memberListData}
                        setCustomer={setCustomer}
                        onSearch={setCustomerSearchTerm}
                        customer={customer}
                        disable={
                          (id &&
                            watcher.transaction_type == SaleStatus.Refund) ||
                            watcher.id ||
                            id
                            ? true
                            : false
                        }
                      />
                    </div>
                    {member == "write" || member == "full_access" ? (
                      <Button
                        access={member == "write" || member == "full_access"}
                        onClick={handleOpenForm}
                        className=" text-white justify-center items-center gap-2"
                      >
                        <i className="fa-regular fa-plus"></i>
                        <span className="text-nowrap">Add Member</span>
                      </Button>
                    ) : (
                      <Button disabled>Add Member</Button>
                    )}
                  </div>
                  {/* {customer && (
                    <div className="text-xs mt-1 flex items-center">
                      {loadingBalance ? (
                        <span className="text-gray-500">
                          Loading balance...
                        </span>
                      ) : tokenBalance !== null ? (
                        <div className=" text-blue-700 px-2 py-1 rounded-sm font-normal w-full text-end">
                          Wallet Balance : {tokenBalance}
                        </div>
                      ) : customer.wallet_address ? (
                        <span className="text-gray-500">
                          Unable to load balance
                        </span>
                      ) : (
                        <span className="text-gray-500">No wallet address</span>
                      )}
                    </div>
                  )} */}

                  <div className="bg-white/90 p-2 space-y-2">
                    <Label className="text-sm font-medium">Member Tax Group</Label>
                    <Select
                      value={selectedMemberTaxGroup?.id?.toString() || "none"}
                      onValueChange={(value) => {
                        if (value === "none") {
                          setSelectedMemberTaxGroup(null);
                        } else {
                          const selected = memberTaxGroups.find(tg => tg.id?.toString() === value);
                          if (selected) {
                            setSelectedMemberTaxGroup(selected);
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue placeholder="Select Member Tax Group" />
                      </SelectTrigger>
                      <SelectContent>
                        {memberTaxGroups.map((tg) => (
                          <SelectItem key={tg.id} value={tg.id?.toString() || ""}>
                            {tg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-white/90">
                    {productPayload.map((product: ExtendedSellableItem) => {
                      const basePrice =
                        product.base_price ?? product.price ?? 0;
                      const taxRate = product.tax_rate || 0;

                      // Calculate original subtotal (before discount and tax)
                      const originalSubtotal = basePrice * product.quantity;

                      // Calculate price after discount (but before tax)
                      const priceAfterDiscount =
                        originalSubtotal - product.discount;

                      // Display price: show discounted price if discount applied, otherwise original price
                      // Always show price BEFORE tax
                      const displayPrice = priceAfterDiscount;

                      return (
                        <Fragment
                          key={`${product.item_id}-${product.item_type}`}
                        >
                          <div className="flex justify-between items-center gap-2 p-2">
                            <div>
                              <p className="capitalize line-clamp-2 break-all">
                                {product.description || "Item"}
                                {product.tax_rate == -1 && (
                                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-primary text-black rounded uppercase">
                                    Donation
                                  </span>
                                )}
                                {product.price_type === "special" && (
                                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 rounded uppercase">
                                    Special: {product.applied_pricing_name || "Pricing"}
                                  </span>
                                )}
                                {product.price_type === "plan" && (
                                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-green-100 text-green-700 rounded uppercase">
                                    Plan: {product.applied_pricing_name || "Pricing"}
                                  </span>
                                )}
                                {product.price_type === "group" && (
                                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-indigo-100 text-indigo-700 rounded uppercase">
                                    Group: {product.applied_pricing_name || "Pricing"}
                                  </span>
                                )}
                              </p>
                              {product.discount > 0 && (
                                <p className="line-through text-sm text-gray-500 text-left">
                                  {formatToPKR(originalSubtotal)}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-center">
                              {product.item_type === POSProductType.Product &&
                                productListData?.find(
                                  (p) => p.id === product.item_id,
                                )?.flexible_price &&
                                productListData?.find(
                                  (p) => p.id === product.item_id,
                                )?.type == ProductType.Wallet ? (
                                <div className="flex flex-col space-y-1 justify-end">
                                  <div className="flex flex-row items-center gap-2">
                                    <label
                                      htmlFor={`flex-price-${product.item_id}`}
                                      className="text-xs text-gray-600 ml-auto"
                                    >
                                      Enter price:
                                    </label>
                                    <FloatingLabelInput
                                      type="number"
                                      id={`flex-price-${product.item_id}`}
                                      value={product.price || ""}
                                      required={true}
                                      placeholder="Price"
                                      min="0"
                                      max="100000"
                                      className="w-24 text-black rounded-sm h-7 py-0 px-1 ml-auto"
                                      onChange={(e) =>
                                        handleFlexiblePriceChange(
                                          product.item_id,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>
                                  <p className="text-sm mt-1 ml-auto">
                                    {product.price && product.price > 0 ? (
                                      <>
                                        {formatToPKR(displayPrice)}
                                        {(taxRate > 0 || taxRate === -1) && (
                                          <span className="text-xs text-gray-500">
                                            {" "}
                                            +
                                            {product.tax_name
                                              ? `${product.tax_name} (${taxRate === -1 ? 0 : taxRate}%)`
                                              : `${taxRate === -1 ? 0 : taxRate}% tax`}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-sm">
                                  {formatToPKR(displayPrice)}
                                  {(taxRate > 0 || taxRate === -1) && (
                                    <span className="text-xs text-gray-500">
                                      {" "}
                                      +
                                      {product.tax_name
                                        ? `${product.tax_name} (${taxRate === -1 ? 0 : taxRate}%)`
                                        : `${taxRate === -1 ? 0 : taxRate}% tax`}
                                    </span>
                                  )}
                                </p>
                              )}
                              {product.item_type === "facilities" ? (
                                <div className="inline-flex items-center ml-auto">
                                  <button
                                    disabled={invoice ? true : false}
                                    onClick={() => {
                                      updateProductQuantity(
                                        product.item_id,
                                        product.item_type,
                                        "decrement",
                                      );
                                    }}
                                    className="disabled:cursor-not-allowed disabled:hover:bg-transparent bg-white rounded border text-gray-600 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 inline-flex items-center justify-center px-2 py-1 w-6 h-6"
                                    title="Remove facility slot"
                                  >
                                    <i className="fa fa-times"></i>
                                  </button>
                                </div>
                              ) : (
                                <div className="inline-flex items-center ml-auto ">
                                  {/* Quantity controls - keeping your existing code */}
                                  <button
                                    disabled={invoice ? true : false}
                                    onClick={() => {
                                      updateProductQuantity(
                                        product.item_id,
                                        product.item_type,
                                        "decrement",
                                      );
                                    }}
                                    className="disabled:cursor-not-allowed disabled:hover:bg-transparent bg-white rounded-l border text-gray-600 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 inline-flex items-center px-2 py-1 border-r border-gray-200"
                                  >
                                    <i className="fa fa-minus"></i>
                                  </button>
                                  <div className="border-t border-b border-gray-100 text-gray-600 hover:bg-gray-100 inline-flex items-center px-4 py-0 select-none">
                                    {product.quantity}
                                  </div>
                                  <button
                                    disabled={
                                      invoice ||
                                        (id &&
                                          watcher.transaction_type ==
                                          SaleStatus.Refund &&
                                          product.quantity ==
                                          maxProductsQauntity[
                                          product.item_id
                                          ]) ||
                                        (product.item_type ===
                                          POSProductType.Event &&
                                          customer &&
                                          !customer.is_business &&
                                          product.quantity >= 1) ||
                                        // Inventory stock limit
                                        (product.item_type === POSProductType.Product && (() => {
                                          const p = productListData?.find(pp => pp.id === product.item_id);
                                          return !!(p?.manage_inventory && p?.quantity !== undefined && product.quantity >= p.quantity);
                                        })())
                                        ? true
                                        : false
                                    }
                                    onClick={() =>
                                      updateProductQuantity(
                                        product.item_id,
                                        product.item_type,
                                        "increment",
                                      )
                                    }
                                    className="disabled:cursor-not-allowed disabled:hover:bg-transparent bg-white rounded-l border text-gray-600 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 inline-flex items-center px-2 py-1 border-r border-gray-200"
                                  >
                                    <i className="fa fa-plus"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <Separator className="h-[1px] font-thin rounded-full" />
                        </Fragment>
                      );
                    })}
                  </div>

                  <div className="rounded-sm bg-white/90 mx-1">
                    <div className="space-y-2 px-2 bg-gray-100 pt-2 ">
                      {/* Tax Type Indicator */}
                      {/* {productPayload.length > 0 && productPayload[0].tax_type && (
                        <div className="w-full flex gap-2 items-center justify-between pb-2 border-b border-gray-200">
                          <p className="text-sm font-medium">Tax Type</p>
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            productPayload[0].tax_type === "inclusive" 
                              ? "bg-blue-100 text-blue-700" 
                              : "bg-green-100 text-green-700"
                          }`}>
                            {productPayload[0].tax_type === "inclusive" ? "Inclusive" : "Exclusive"}
                          </span>
                        </div>
                      )} */}

                      {/* OVERALL SUMMARY */}
                      {/* Net total before discount (before name was subtotal) */}
                      <div className="w-full flex gap-2  items-center justify-between">
                        <p>Net price</p>
                        <p>{formatToPKR(watcher.sub_total)}</p>{" "}
                        {/* Display Subtotal */}
                      </div>

                      <div className="w-full flex gap-2 items-center justify-between">
                        <p>Discount</p>
                        <div className="flex flex-col items-end">
                          <FloatingLabelInput
                            type="number"
                            id="discount"
                            defaultValue={watcher.discount}
                            step="1"
                            min={0}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            onKeyDown={(e) => {
                              if (e.key === "." || e.key === ",") {
                                e.preventDefault();
                              }
                              // Prevent arrow keys from exceeding bounds
                              const productDiscountTotal =
                                productPayload.reduce(
                                  (total, product: ExtendedSellableItem) => {
                                    const percentage =
                                      product.discount_percentage || 0;
                                    const basePrice =
                                      product.base_price ?? product.price ?? 0;
                                    const subtotal =
                                      basePrice * product.quantity;
                                    return (
                                      total + (subtotal * percentage) / 100
                                    );
                                  },
                                  0,
                                );
                              const minAllowed =
                                Math.round(productDiscountTotal);
                              const maxAllowed = Math.max(
                                0,
                                Math.round(watcher.sub_total || 0) - 1,
                              );
                              const current =
                                Number(
                                  (e.currentTarget as HTMLInputElement).value,
                                ) || 0;
                              if (
                                (e.key === "ArrowUp" &&
                                  current >= maxAllowed) ||
                                (e.key === "ArrowDown" && current <= minAllowed)
                              ) {
                                e.preventDefault();
                              }
                            }}
                            disabled={
                              (Number(id) &&
                                watcher.transaction_type ==
                                SaleStatus.Refund) ||
                              hide_pos_discount == "no_access" ||
                              productPayload.length == 0
                            }
                            placeholder="Enter discount amount"
                            className="w-fit text-black rounded-sm text-right"
                            {...register("discount", {
                              setValueAs: (value) => {
                                // Handle empty string or invalid input
                                if (
                                  value === "" ||
                                  value === null ||
                                  value === undefined
                                ) {
                                  return 0;
                                }
                                const numValue = Number(value);
                                return isNaN(numValue) ? 0 : numValue;
                              },
                              min: 0,
                              validate: (value) => validateDiscount(value ?? 0),
                              onChange: (e) => {
                                const inputValue = (
                                  e?.target as HTMLInputElement
                                )?.value;
                                const enteredDiscount = Number(inputValue) || 0;

                                let nextIsManuallySet = isDiscountManuallySet;

                                if (inputValue === "") {
                                  // Clear: revert to product discounts
                                  nextIsManuallySet = false;
                                  setIsDiscountManuallySet(false);
                                } else if (enteredDiscount > 0) {
                                  // Determine if we should mark as manual
                                  const productDiscountTotal =
                                    productPayload.reduce(
                                      (
                                        total,
                                        product: ExtendedSellableItem,
                                      ) => {
                                        const percentage =
                                          product.discount_percentage || 0;
                                        const basePrice =
                                          product.base_price ??
                                          product.price ??
                                          0;
                                        const subtotal =
                                          basePrice * product.quantity;
                                        return (
                                          total + (subtotal * percentage) / 100
                                        );
                                      },
                                      0,
                                    );

                                  if (
                                    Math.abs(
                                      enteredDiscount - productDiscountTotal,
                                    ) > 0.01
                                  ) {
                                    nextIsManuallySet = true;
                                    setIsDiscountManuallySet(true);
                                  }
                                }

                                // Trigger real-time recalculation immediately
                                if (productPayload.length > 0) {
                                  updateFormValues(productPayload, {
                                    clampDiscount: false,
                                    manualDiscountOverride: enteredDiscount,
                                    isDiscountManuallySetOverride:
                                      nextIsManuallySet,
                                  });
                                }
                              },
                              onBlur: (e) => {
                                const inputValue = (
                                  e?.target as HTMLInputElement
                                )?.value;

                                // If field is empty, set to minimum allowed (product discount)
                                if (
                                  inputValue === "" ||
                                  inputValue === null ||
                                  inputValue === undefined
                                ) {
                                  const productDiscountTotal =
                                    productPayload.reduce(
                                      (
                                        total,
                                        product: ExtendedSellableItem,
                                      ) => {
                                        const percentage =
                                          product.discount_percentage || 0;
                                        const basePrice =
                                          product.base_price ??
                                          product.price ??
                                          0;
                                        const subtotal =
                                          basePrice * product.quantity;
                                        return (
                                          total + (subtotal * percentage) / 100
                                        );
                                      },
                                      0,
                                    );
                                  const minAllowed =
                                    Math.round(productDiscountTotal);
                                  setValue("discount", minAllowed, {
                                    shouldValidate: true,
                                  });
                                  setIsDiscountManuallySet(false);

                                  // Recalculate with the reset value
                                  if (productPayload.length > 0) {
                                    updateFormValues(productPayload, {
                                      clampDiscount: true,
                                      manualDiscountOverride: minAllowed,
                                      isDiscountManuallySetOverride: false,
                                    });
                                  }
                                  return;
                                }

                                const productDiscountTotal =
                                  productPayload.reduce(
                                    (total, product: ExtendedSellableItem) => {
                                      const percentage =
                                        product.discount_percentage || 0;
                                      const basePrice =
                                        product.base_price ??
                                        product.price ??
                                        0;
                                      const subtotal =
                                        basePrice * product.quantity;
                                      return (
                                        total + (subtotal * percentage) / 100
                                      );
                                    },
                                    0,
                                  );
                                const minAllowed =
                                  Math.round(productDiscountTotal);
                                const maxAllowed = Math.max(
                                  0,
                                  Math.round(watcher.sub_total || 0) - 1,
                                );
                                const entered = Number(inputValue) || 0;
                                const clamped = Math.min(
                                  Math.max(entered, minAllowed),
                                  maxAllowed,
                                );

                                let finalIsManuallySet = false;
                                if (Math.abs(clamped - minAllowed) > 0.01) {
                                  finalIsManuallySet = true;
                                  setIsDiscountManuallySet(true);
                                } else {
                                  finalIsManuallySet = false;
                                  setIsDiscountManuallySet(false);
                                }

                                // Final update with clamped value
                                setValue("discount", clamped, {
                                  shouldValidate: true,
                                });
                                if (productPayload.length > 0) {
                                  updateFormValues(productPayload, {
                                    clampDiscount: true,
                                    manualDiscountOverride: clamped,
                                    isDiscountManuallySetOverride:
                                      finalIsManuallySet,
                                  });
                                }
                              },
                            })}
                          />
                          {discountError && (
                            <p className="text-xs text-red-500 mt-1 text-right max-w-[200px]">
                              {discountError}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="w-full flex gap-2 items-center justify-between">
                        {/* newly showing, the net price after discount */}
                        <p>Subtotal</p>
                        <p>
                          {formatToPKR(
                            Number(watcher.sub_total) -
                            (Number(watcher.discount) ?? 0),
                          )}
                        </p>
                      </div>

                      <div className="w-full flex gap-2 items-center justify-between">
                        <p>
                          Tax{" "}
                          {productPayload.length > 0 &&
                            (productPayload[0] as ExtendedSellableItem)
                              .tax_type === "inclusive" && (
                              <span className="text-xs text-gray-500">
                                (included)
                              </span>
                            )}
                          {(() => {
                            // Get unique tax names from items that have tax applied
                            const taxNames = Array.from(
                              new Set(
                                productPayload
                                  .filter(
                                    (item: ExtendedSellableItem) =>
                                      item.tax_name &&
                                      item.tax_name.trim() !== "" &&
                                      (item.tax_amount || 0) > 0,
                                  )
                                  .map(
                                    (item: ExtendedSellableItem) =>
                                      item.tax_name,
                                  ),
                              ),
                            );

                            return taxNames.length > 0 ? (
                              <span className="text-xs text-gray-600 block">
                                ({taxNames.join(", ")})
                              </span>
                            ) : null;
                          })()}
                        </p>
                        <p>{formatToPKR(watcher.tax_amount)}</p>{" "}
                        {/* Display Tax */}
                      </div>

                      <div className="w-full flex gap-2 items-center justify-between font-bold">
                        <p>Total</p>
                        <p>
                          {formatToPKR(
                            (id && watcher.transaction_type == SaleStatus.Refund
                              ? -Number(watcher.total)
                              : watcher.total) as number,
                          )}
                        </p>{" "}
                        {/* Display Total */}
                      </div>
                      <Separator className="my-2" />
                      <div className="w-full flex gap-2 items-center justify-between font-bold">
                        <p>Processed By</p>
                        <div>
                          <Popover
                            open={openStaffDropdown}
                            onOpenChange={setStaffDropdown}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                className="
                                
                                    w-[200px]
                                
                                font-normal capitalize  hover:bg-transparent border-[1px] shadow-sm bg-white "
                                aria-expanded={openStaffDropdown}
                              >
                                <span className="w-full text-left font-normal">
                                  {watcher.staff_id
                                    ? staffList?.find(
                                      (staff) =>
                                        staff.value == watcher.staff_id,
                                    )?.label
                                    : "Select Staff"}
                                </span>
                                <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 relative "
                              side="bottom"
                            >
                              <Command className="w-full ">
                                <CommandList>
                                  <CommandInput
                                    placeholder="Select Staff"
                                    className="font-normal"
                                  />
                                  <CommandEmpty>
                                    No Staff matched the Search.
                                  </CommandEmpty>
                                  <CommandGroup className="">
                                    {staffList &&
                                      staffList.map((staff: any) => (
                                        <CommandItem
                                          value={staff.label}
                                          key={staff.value}
                                          className="font-normal"
                                          onSelect={() => {
                                            setValue(
                                              "staff_id",
                                              Number(staff.value),
                                            );
                                            setValue("staff_name", staff.label);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                              staff.value == watcher.staff_id
                                                ? "opacity-100"
                                                : "opacity-0",
                                            )}
                                          />
                                          {staff.label}
                                        </CommandItem>
                                      ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <Button
                        className="w-full bg-primary text-black rounded-sm"
                        onClick={paymentCheckout}
                        disabled={discountError !== ""}
                      >
                        {id && watcher.transaction_type == SaleStatus.Refund
                          ? "Refund"
                          : "Checkout"}
                      </Button>

                      {/* <Button
                        className="w-full bg-primary text-black rounded-sm"
                        onClick={handleGetWallet}
                      >
                        Get wallet Address
                      </Button> */}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {showCheckout && (
            <Checkout
              showCheckout={showCheckout}
              initialValues={initialValues}
              setShowCheckout={setShowCheckout}
              productPayload={productPayload as any}
              setCustomer={setCustomer}
              setProductPayload={setProductPayload as any}
              setInputValue={setInputValue}
              customer={customer}
              watcher={watcher}
              refetchMemberships={refetchMemberships}
              refetchProducts={refetchProducts}
              refetchEvents={refetchEvents}
            />
          )}

          <DayExceeded
            isOpen={dayExceeded}
            onClose={handleCloseDayExceeded}
            onContinue={handleContinueDayExceeded}
            closeModal={() => setDayExceeded(false)}
          />
        </div>

        <ParkReceipt
          isOpen={openParkSale}
          onClose={() => setParkSale(false)}
          parkSale={parkSale}
        />
      </FormProvider>

      {openMemberForm && (
        <MemberForm
          open={openMemberForm}
          setOpen={setOpenMemberForm}
          memberData={editMember}
          setMemberData={setEditMember}
          action={action}
          setAction={setAction}
          refetch={memberRefetch}
          breadcrumb="POS"
        />
      )}
    </div>
  );
};

export default Sell;

interface DayExceededProps {
  isOpen: boolean;
  onClose: any;
  onContinue: any;
  closeModal: any;
}

export function DayExceeded({
  isOpen,
  onClose,
  onContinue,
  closeModal,
}: DayExceededProps) {
  return (
    <AlertDialog open={isOpen} onOpenChange={closeModal}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Register is Open more than 24 Hours
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            Do you want to continue with this register session or close to
            create a new session?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Close</AlertDialogCancel>
          <AlertDialogCancel
            className="bg-primary text-black border-transparent hover:"
            onClick={onContinue}
          >
            Continue
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface ComboboxTypes {
  list?: Transaction[];
  customerList?: MemberTableDatatypes[];
  setCustomer: any;
  setTransactionId?: any;
  customer: any;
  label?: string;
  disable?: boolean;
  setWatcherValue: UseFormSetValue<Transaction>;
  setProductPayload: React.Dispatch<
    React.SetStateAction<ExtendedSellableItem[]>
  >;
  refetch?: any;
  reset?: any;
  isLoadingDataRef?: React.MutableRefObject<boolean>;
  setSelectedMemberTaxGroup: (taxGroup: TaxGroup | null) => void;
  memberTaxGroups: TaxGroup[];
}

export function RetriveSaleCombobox({
  list,
  setCustomer,
  customer,
  label,
  customerList,
  setTransactionId,
  setWatcherValue,
  setProductPayload,
  refetch,
  reset,
  isLoadingDataRef,
  setSelectedMemberTaxGroup,
  memberTaxGroups,
}: ComboboxTypes) {
  const modifiedList = list?.map((item: any) => ({
    ...item,
    transactionId: item.id,
    value: item.member_id,
    label: item.member_name,
  }));
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="disabled:cursor-not-allowed capitalize w-full justify-center items-center gap-2  bg-white rounded-sm border-[1px]"
        >
          <i className="fa fa-share"></i>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className=" min-w-[25rem] p-0" side="bottom">
        <Command>
          <CommandList className="">
            <CommandEmpty>No customer found.</CommandEmpty>
            {modifiedList && modifiedList.length > 0 && (
              <>
                <div className="flex justify-between gap-3 px-3 py-1 items-center font-semibold">
                  <p>Items</p>
                  <p>Customer</p>
                </div>
                <Separator className="mx-2 w-96" />
              </>
            )}
            <CommandGroup className="">
              {modifiedList?.map((modCustomer) => (
                <CommandItem
                  key={modCustomer.transactionId + ""}
                  value={modCustomer.transactionId + ""}
                  onSelect={(currentValue) => {
                    // find member_id from transaction list
                    const transaction = modifiedList.find(
                      (item) => item.transactionId == currentValue,
                    );
                    setValue(currentValue);
                    const customer = customerList?.find(
                      (item: any) => item.id == transaction.member_id,
                    );
                    if (isLoadingDataRef) isLoadingDataRef.current = true;
                    setCustomer(customer);
                    setTransactionId(currentValue);
                    // Convert transaction items to ExtendedSellableItem format
                    const extendedItems = transaction.items.map(
                      (item: any) => ({
                        ...item,
                        base_price: item.price || item.base_price,
                        description: item.description || "Item",
                        tax_rate: item.tax_rate || 0,
                        tax_type: item.tax_type || "exclusive",
                        discount_percentage: item.discount_percentage || 0,
                        // For retrive sale combobox, preserve the original discount amount
                        discount: item.discount || 0,
                      }),
                    );
                    setProductPayload(extendedItems as any);
                    reset(transaction);

                    // Update selectedMemberTaxGroup based on transaction.member_tax_group_id
                    if (transaction.member_tax_group_id) {
                      const selectedTaxGroup = memberTaxGroups.find(
                        (tg) => tg.id === transaction.member_tax_group_id,
                      );
                      if (selectedTaxGroup) {
                        setSelectedMemberTaxGroup(selectedTaxGroup);
                      }
                    } else {
                      setSelectedMemberTaxGroup(null);
                    }

                    // Use timeout to reset loading flag after state has settled
                    if (isLoadingDataRef) {
                      setTimeout(() => {
                        isLoadingDataRef.current = false;
                      }, 100);
                    }
                    setOpen(false);
                  }}
                  className="cursor-pointer flex flex-col items-start"
                >
                  <div className="flex gap-3 text-xs  justify-between items-center w-full">
                    <div>
                      <div className="flex gap-1">
                        {modCustomer.items
                          .slice(0, 1)
                          .map((list: any, index: number) => (
                            <div key={index}>
                              <span>{list.quantity}</span> x{" "}
                              <span className="capitalize">
                                {list.description || "Item"}
                              </span>
                            </div>
                          ))}

                        {modCustomer.items.length > 1 && (
                          <div className="text-gray-500">
                            +{modCustomer.items.length - 1} more
                          </div>
                        )}
                      </div>
                      <p className="text-xs">
                        Parked by {displayDate(modCustomer.transaction_date)},
                        by{" "}
                        <span className="capitalize">
                          {modCustomer.staff_name}
                        </span>
                      </p>
                    </div>
                    <p className="capitalize"> {modCustomer.label} </p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const encryptSecretKey = (secretKey: string) => {
  // Convert the secretKey string to Base64
  return btoa(secretKey);
};

export const getWallet = async (id: number) => {
  try {
    // const encryptedSecretKey = encryptSecretKey(VITE_SECRET_KEY);

    const encryptedSecretKey = encryptSecretKey(
      import.meta.env.VITE_SECRET_KEY,
    );
    const response = await fetch(
      `${VITE_BLOCKCHAIN_API_URL}/get-wallet?id=${id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-secret-key": encryptedSecretKey,
        },
      },
    );
    console.log("encription secrey", encryptedSecretKey);
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to get wallet:", error);
    throw error;
  }
};

export const mintToken = async (data: {
  user_id: number;
  transaction_id: number;
  invoice_id: number;
  jwt_token: string;
  org_id: number;
  address: string;
  amount: number;
  payment_method: string;
  product_name: string;
  product_description: string;
}) => {
  try {
    const {
      user_id,
      transaction_id,
      invoice_id,
      jwt_token,
      org_id,
      address,
      amount,
      payment_method,
      product_name,
      product_description,
    } = data;

    const encryptedSecretKey = encryptSecretKey(
      import.meta.env.VITE_SECRET_KEY,
    );

    const response = await fetch(`${VITE_BLOCKCHAIN_API_URL}/mint-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-secret-key": encryptedSecretKey,
      },

      body: JSON.stringify({
        user_id,
        transaction_id,
        invoice_id,
        jwt_token,
        org_id,
        address,
        amount,
        payment_method,
        product_name,
        product_description,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to mint token");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to mint token:", error);
    throw error;
  }
};

export const burnToken = async (data: {
  org_id: number;
  address: string;
  amount: number;
}) => {
  try {
    const { org_id, address, amount } = data;

    const encryptedSecretKey = encryptSecretKey(
      import.meta.env.VITE_SECRET_KEY,
    );

    const response = await fetch(`${VITE_BLOCKCHAIN_API_URL}/burn-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-secret-key": encryptedSecretKey,
      },
      body: JSON.stringify({
        org_id,
        address_from: address,
        amount,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to burn token");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to burn token:", error);
    throw error;
  }
};

export const getTokenBalance = async (data: {
  userAddress: string;
  orgId: number;
}) => {
  try {
    const { userAddress, orgId } = data;

    const encryptedSecretKey = encryptSecretKey(
      import.meta.env.VITE_SECRET_KEY,
    );

    const response = await fetch(
      `${VITE_BLOCKCHAIN_API_URL}/balance/${userAddress}/${orgId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-secret-key": encryptedSecretKey,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to retrieve token balance");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to retrieve token balance:", error);
    throw error;
  }
};
